// @ts-nocheck
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import { toast } from 'sonner';
import _ from 'lodash';
import helpers from '@/utils/helpers';

import useTimeSlotStore from '@store/timeSlotStore';
import useGameStore from '@store/gameStore';
import useDivisionStore from '@store/divisionStore';
import useTeamDivisionStore from '@store/teamDivisionStore';
import useLeagueStore from '@store/leagueStore';
import useLocationStore from '@store/locationStore';
import LocationCombobox from '@/components/LocationCombobox/LocationCombobox';
import BracketTree from '@/components/BracketTree/BracketTree';
import useRegistrationStore from '@store/registrationStore';
import useTournamentStore from '@store/tournamentStore';
import { axios as authedAxios } from '@store/axios';
import { buildPlayerPayload } from '@/utils/playerFields';
import generateAutoTeamName from '@staty/shared/utils/generateAutoTeamName';
import { isIndividualSport } from '@staty/shared/utils/sportCategory';
import { isParseableTime } from '@staty/shared/utils/gameDateTime';
import DivisionLayoutEditor from './DivisionLayoutEditor';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, CalendarDays, Trash2, GripVertical, Pencil, BarChart3, Download, ListChecks, Camera, Upload, ExternalLink, LayoutGrid, Table2, ListOrdered } from 'lucide-react';
import GamesScoreTable from './GamesScoreTable';
import DivisionStandings from './DivisionStandings';
import resolveStandingsSport from '@staty/shared/utils/resolveStandingsSport';
import { publicSiteUrl } from '@/utils/publicSiteUrl';
import { publicDivisionPath, publicDivisionStandingsPath } from '@staty/shared/utils/publicPaths';
import ScheduleUploadDialog from './ScheduleUploadDialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { generateScoresheet, SCORESHEET_VARIANTS, hasScoresheet } from '@/utils/scoresheets';
import { buildBasketballScoresheetDoc, basketballScoresheetFilename, BASKETBALL_SCORESHEET_VARIANTS } from '@/utils/scoresheets/basketball';
import { saveDoc } from '@/utils/scoresheets/pdfCommon';
import { resolveMatchups, canAutoPopulate, resolveBracketGames, resolvePlayoffBracketGames, canAutoPopulateBracket } from '@/utils/scoresheets/matchups';
import { getPlayoffSlots, getPlayoffSlotsForFormat, defaultPlayoffTeams, PlayoffSlot } from '@/utils/playoffSlots';
import { isScoresheetSupportedSport } from '@staty/shared/utils/scoresheetSupport';
import { isAfterRegistrationEnd } from '@staty/shared/utils/registrationSchedule';
// Shared with /admin/game-results (UpcomingGamesAdmin) — single-source scan +
// results-entry UI; changes there apply to both pages.
import ScoresheetScanDialogs from '@containers/UpcomingGamesAdmin/ScoresheetScanDialogs';
import GameResultRow from '@containers/UpcomingGamesAdmin/GameResultRow';
import { UNASSIGNED_LANE_ID, appendUnassignedLane, isRealLocationId } from './scheduleLanes';
import { partitionFormatChangeGames } from './formatChangeCleanup';
import { isConsolationFormat, normalizeBracketSize, placeholderFromSlots } from '@staty/shared/utils/bracketTopology';
import { createTournamentGameStatsLink, createLeagueGameStatsLink } from '@/lib/createLinks';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import getLocationDisplayName from '@staty/shared/utils/getLocationDisplayName';
import getTeamDisplayName from '@staty/shared/utils/getTeamDisplayName';
import isUnseededTeam from '@staty/shared/utils/isUnseededTeam';
import getConfig from '@staty/shared/utils/getConfig';
import getDefaultGameDuration from '@staty/shared/utils/getDefaultGameDuration';
import { deriveDivisionTeamStates } from '@staty/shared/utils/qualifiedTeams';

// ─── Constants ────────────────────────────────────────────────────────────────
const SNAP_MINUTES = 15;
const SLOT_HEIGHT = 22;
const TIME_GUTTER_WIDTH = 80;
const COLUMN_WIDTH = 260; // minimum court-column width; columns grow to fill available width
const MIN_CARD_WIDTH = 120; // narrowest an overlapping game card may get before hiding with "+N more"
const DEFAULT_DURATION = 60;
const MAX_VISIBLE_COLUMNS = 3; // fallback overlap cap before the container width is measured
const GAME_GAP = 4;
const GAME_INSET = 4;

const TOURNAMENT_FORMATS = [
  { value: 'round-robin',          label: 'Round Robin',               description: 'Recommended for 3–8 teams',   minTeams: 3, tooltip: 'Every team plays every other team; best record wins.' },
  { value: 'round-robin-playoffs', label: 'Round Robin with Playoffs', description: 'Recommended for 3–8 teams',   minTeams: 3, tooltip: 'Every team plays everyone, then the top teams advance to a playoff bracket.' },
  { value: 'bracket',              label: 'Bracket',                   description: 'Requires exactly 8 teams',    minTeams: 8, tooltip: "Single-elimination bracket: lose once and you're out." },
  { value: 'pool-play',            label: 'Pool Play',                 description: 'Recommended for 6–16 teams',  minTeams: 6, tooltip: 'Teams are split into pools and play the others in their pool.' },
  { value: 'pool-play-playoffs',   label: 'Pool Play with Playoffs',   description: 'Recommended for 6–16 teams',  minTeams: 6, tooltip: 'Teams play within their pool, then top finishers advance to a playoff bracket.' },
  { value: 'playoffs-consolation', label: 'Bracket with Consolation',  description: 'Every team gets a final placement', minTeams: 4, tooltip: 'Single-elimination plus a consolation bracket: quarterfinal losers keep playing to decide 5th–8th place.' },
] as const;

const REGULAR_FORMATS = [
  { value: '',         label: 'None',     description: 'No bracket/schedule format',  minTeams: 0, tooltip: 'No bracket or schedule format for this division.' },
  { value: 'playoffs', label: 'Playoffs', description: 'Single-elimination playoffs', minTeams: 0, tooltip: "Single-elimination playoffs: lose once and you're out." },
  { value: 'playoffs-consolation', label: 'Playoffs + Consolation', description: 'Every team gets a final placement', minTeams: 4, tooltip: 'Single-elimination plus a consolation bracket: quarterfinal losers keep playing to decide 5th–8th place.' },
] as const;

// Playoff bracket-size choices, labeled by the placements each awards. Persisted
// to division.config.playoffTeams; drives the on-screen playoff column + the PDF.
const PLAYOFF_SIZE_OPTIONS = [
  { value: 2, label: 'Final only (1st, 2nd)' },
  { value: 4, label: 'Semifinals + 3rd place (1st–4th)' },
  { value: 8, label: 'Quarterfinals (1st–8th)' },
] as const;

function distributeTeamsSerpentine(teams: any[], numPools: number): any[][] {
  const pools: any[][] = Array.from({ length: numPools }, () => []);
  for (let i = 0; i < teams.length; i++) {
    const roundIdx = Math.floor(i / numPools);
    const posInRound = i % numPools;
    const poolIdx = roundIdx % 2 === 0 ? posInRound : numPools - 1 - posInRound;
    pools[poolIdx].push(teams[i]);
  }
  return pools;
}

// ─── Utilities ────────────────────────────────────────────────────────────────
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const parseTimeToMinutes = (timeString: string) => {
  if (!timeString) return 0;
  const twelve = timeString.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (twelve) {
    let hours = parseInt(twelve[1], 10);
    const minutes = parseInt(twelve[2], 10);
    const period = twelve[3].toUpperCase();
    if (period === 'AM' && hours === 12) hours = 0;
    if (period === 'PM' && hours !== 12) hours += 12;
    return hours * 60 + minutes;
  }
  const twentyFour = timeString.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFour) return parseInt(twentyFour[1], 10) * 60 + parseInt(twentyFour[2], 10);
  return 0;
};

const formatMinutesForGameTime = (minutes: number) => {
  const h24 = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return `${h12}:${mins.toString().padStart(2, '0')} ${ampm}`;
};

const formatTimeLabel = (minutes: number) => {
  const h24 = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return mins === 0 ? `${h12} ${ampm}` : `${h12}:${mins.toString().padStart(2, '0')} ${ampm}`;
};

const toDateKey = (raw: string | Date) => {
  if (!raw) return '';
  const asString = typeof raw === 'string' ? raw : raw.toISOString();
  const mdY = asString.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (mdY) {
    return `${mdY[3]}-${mdY[1].padStart(2, '0')}-${mdY[2].padStart(2, '0')}`;
  }
  const parsed = new Date(asString);
  if (Number.isNaN(parsed.getTime())) return '';
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const toGlobalDate = (dateKey: string) => {
  if (!dateKey) return '';
  const [yyyy, mm, dd] = dateKey.split('-');
  return `${mm}-${dd}-${yyyy}`;
};

const formatDateForSelect = (dateKey: string) => {
  if (!dateKey) return '';
  const parsed = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateKey;
  return parsed.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const getLocationIdFromGame = (game: any, locations: any[]) => {
  const byModel = helpers.getObjId(game.locationModel);
  if (byModel) return byModel;
  const gameLocationObjId = helpers.getObjId(game.location);
  if (gameLocationObjId) return gameLocationObjId;
  if (typeof game.location === 'string') {
    const byName = locations.find(
      (loc) => loc?.name === game.location || getLocationDisplayName(loc) === game.location,
    );
    if (byName) return byName._id;
  }
  return null;
};

const getTimeSlotForGame = (timeSlots: any[], dateKey: string, locationId: string, startMinutes: number) => {
  return timeSlots.find((slot) => {
    const slotDateKey = toDateKey(slot.availableDate);
    if (slotDateKey !== dateKey) return false;
    if (helpers.getObjId(slot.location) !== locationId) return false;
    const start = parseTimeToMinutes(slot.startTime);
    const end = parseTimeToMinutes(slot.endTime);
    return startMinutes >= start && startMinutes < end;
  });
};

const getTeamName = (teamRef: any, teams: any[], placeholder?: string) => {
  const teamId = helpers.getObjId(teamRef);
  if (!teamId) return placeholder || 'TBD';
  const found = teams.find((t) => t._id === teamId);
  return found ? getTeamDisplayName(found) : (placeholder || 'TBD');
};

const buildOverlapLayout = (
  games: any[],
  draftByGameId: Record<string, { start: number; duration: number }>,
  maxVisibleColumns: number = MAX_VISIBLE_COLUMNS,
  defaultDuration: number = DEFAULT_DURATION,
) => {
  const events = games
    .map((game) => {
      const draft = draftByGameId[game._id];
      const start = draft?.start ?? parseTimeToMinutes(game.time);
      const duration = draft?.duration ?? (game.duration || defaultDuration);
      const end = start + duration;
      return { game, start, duration, end };
    })
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const groups: { start: number; end: number; events: any[] }[] = [];
  let current: { start: number; end: number; events: any[] } | null = null;

  events.forEach((event) => {
    if (!current || event.start >= current.end) {
      if (current) groups.push(current);
      current = { start: event.start, end: event.end, events: [event] };
      return;
    }
    current.events.push(event);
    current.end = Math.max(current.end, event.end);
  });
  if (current) groups.push(current);

  const items: any[] = [];
  const groupInfo: { start: number; end: number; hiddenCount: number; columnCount: number }[] = [];

  groups.forEach((group) => {
    const columnsEnd: number[] = [];
    const groupItems = group.events.sort((a, b) => a.start - b.start || a.end - b.end);
    const laidOut: any[] = [];

    groupItems.forEach((event) => {
      let columnIndex = columnsEnd.findIndex((end) => end <= event.start);
      if (columnIndex === -1) {
        columnIndex = columnsEnd.length;
        columnsEnd.push(event.end);
      } else {
        columnsEnd[columnIndex] = event.end;
      }
      laidOut.push({ ...event, column: columnIndex, columnCount: 0, groupStart: group.start });
    });

    const columnCount = columnsEnd.length || 1;
    let hiddenCount = 0;
    laidOut.forEach((item) => {
      item.columnCount = columnCount;
      if (item.column >= maxVisibleColumns) hiddenCount += 1;
    });

    items.push(...laidOut);
    groupInfo.push({ start: group.start, end: group.end, hiddenCount, columnCount });
  });

  return { items, groups: groupInfo };
};

// ─── Registration field renderer ──────────────────────────────────────────────
function RegistrationFieldInput({ field, value, onChange }: { field: any; value: any; onChange: (v: any) => void }) {
  if (field.fieldType === 'select') {
    return (
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={`Select ${field.name}`} />
        </SelectTrigger>
        <SelectContent>
          {(field.options || []).map((opt: string) => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (field.fieldType === 'textarea') {
    return (
      <Textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.helperText || field.name}
      />
    );
  }
  return (
    <Input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.helperText || field.name}
    />
  );
}

// ─── Round Robin sub-components ───────────────────────────────────────────────
const GameCard = React.memo(function GameCard({ game, teamDivisions, onEditGame, onDragStart, isDragPreview, isDragging }: {
  game: any; teamDivisions: any[]; onEditGame: (g: any) => void;
  onDragStart: (e: React.MouseEvent, gameId: string) => void;
  isDragPreview?: boolean;
  isDragging?: boolean;
}) {
  const home = getTeamName(game.homeTeam, teamDivisions, game.homeTeamPlaceholder);
  const away = getTeamName(game.awayTeam, teamDivisions, game.awayTeamPlaceholder);
  return (
    <div className={`border border-primary bg-primary/10 rounded-md select-none flex items-stretch${isDragPreview ? ' opacity-60' : ''}${isDragging ? ' opacity-40' : ''}`}>
      <div
        className="flex items-center px-1.5 cursor-grab active:cursor-grabbing text-primary/40 hover:text-primary border-r border-primary/30"
        onMouseDown={(e) => onDragStart(e, game._id)}
      >
        <GripVertical size={14} />
      </div>
      <div className="flex-1 px-2 py-2 min-w-0">
        {/* Home on line 1, away on line 2 so both teams are visible (long doubles
            names were hidden when shown as one truncated "home vs away" line). */}
        <div className="text-xs font-semibold text-primary-deep truncate">{home}</div>
        <div className="text-xs font-semibold text-primary-deep truncate">{away}</div>
        {game.time && <div className="text-[11px] text-muted-foreground">{game.time}</div>}
        {game.location && <div className="text-[11px] text-muted-foreground/60 truncate">{game.location}</div>}
      </div>
      <button
        className="flex items-center px-1.5 text-primary/40 hover:text-primary border-l border-primary/30"
        onClick={(e) => { e.stopPropagation(); onEditGame(game); }}
      >
        <Pencil size={12} />
      </button>
    </div>
  );
});

const GameSlot = React.memo(function GameSlot({ slotId, game, teamDivisions, onEditGame, onDragStart, isDragPreview }: {
  slotId: string; game: any | null; teamDivisions: any[]; onEditGame: (g: any) => void;
  onDragStart: (e: React.MouseEvent, gameId: string) => void;
  isDragPreview?: boolean;
}) {
  return (
    <div data-slot-id={slotId} className={`min-h-[60px] rounded-md border-2 bg-muted/50 ${isDragPreview ? 'border-solid border-primary' : 'border-dashed border-border'}`}>
      {game
        ? <GameCard game={game} teamDivisions={teamDivisions} onEditGame={onEditGame} onDragStart={onDragStart} isDragPreview={isDragPreview} />
        : <div className="flex items-center justify-center h-full min-h-[60px] text-xs text-muted-foreground/60">Drop game here</div>
      }
    </div>
  );
});

const RoundColumn = React.memo(function RoundColumn({ round, gamesPerRound, getSlotGame, teamDivisions, onEditGame, onDragStart, hoverSlotId, poolId }: {
  round: number; gamesPerRound: number;
  getSlotGame: (round: number, gameNumber: number) => any | undefined;
  teamDivisions: any[]; onEditGame: (g: any) => void;
  onDragStart: (e: React.MouseEvent, gameId: string) => void;
  hoverSlotId: string | null;
  poolId?: number;
}) {
  return (
    <div className="flex-shrink-0 w-52 space-y-2">
      <div className="text-xs font-semibold text-muted-foreground text-center pb-1 border-b">Round {round}</div>
      {Array.from({ length: gamesPerRound }, (_, i) => i + 1).map((gameNumber) => {
        const slotId = poolId != null ? `slot-p${poolId}-r${round}-g${gameNumber}` : `slot-r${round}-g${gameNumber}`;
        return (
          <GameSlot key={gameNumber} slotId={slotId}
            game={getSlotGame(round, gameNumber) ?? null}
            teamDivisions={teamDivisions} onEditGame={onEditGame} onDragStart={onDragStart}
            isDragPreview={hoverSlotId === slotId}
          />
        );
      })}
    </div>
  );
});

const UnassignedDropZone = React.memo(function UnassignedDropZone({ games, teamDivisions, onEditGame, onDragStart, hoverSlotId, dragGameId }: {
  games: any[]; teamDivisions: any[]; onEditGame: (g: any) => void;
  onDragStart: (e: React.MouseEvent, gameId: string) => void;
  hoverSlotId: string | null;
  dragGameId: string | null;
}) {
  return (
    <div data-slot-id="unassigned" className="min-h-[60px] rounded-md border-2 border-dashed border-border p-2 flex flex-wrap gap-2">
      {games.map((game) => (
        <GameCard key={game._id} game={game} teamDivisions={teamDivisions} onEditGame={onEditGame} onDragStart={onDragStart}
          isDragPreview={hoverSlotId === 'unassigned'}
          isDragging={game._id === dragGameId} />
      ))}
      {games.length === 0 && <div className="text-xs text-muted-foreground/60 self-center">Drop here to unassign</div>}
    </div>
  );
});

// ─── Pool Play sub-components ─────────────────────────────────────────────────
const PoolSection = React.memo(function PoolSection({ pool, poolTeams, getSlotGame, teamDivisions, onEditGame, onDragStart, hoverSlotId, onCreateGame, canCreateGame, roundsPerMatchup }: {
  pool: { id: number; name: string; teamIds: string[] };
  poolTeams: any[];
  getSlotGame: (pool: number, round: number, gameNumber: number) => any;
  teamDivisions: any[];
  onEditGame: (g: any) => void;
  onDragStart: (e: React.MouseEvent, gameId: string) => void;
  hoverSlotId: string | null;
  onCreateGame: () => void;
  canCreateGame: boolean;
  roundsPerMatchup: number;
}) {
  const n = poolTeams.length;
  const isEven = n % 2 === 0;
  const numRounds = n < 2 ? 0 : (isEven ? n - 1 : n) * roundsPerMatchup;
  const gamesPerRound = n < 2 ? 0 : isEven ? n / 2 : Math.floor(n / 2);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between border-b pb-1">
        <div className="text-sm font-semibold text-foreground">
          {pool.name} · {n} teams · {numRounds} round{numRounds !== 1 ? 's' : ''}
        </div>
        <Button size="sm" variant="outline" onClick={onCreateGame} disabled={!canCreateGame}>
          <Plus size={12} className="mr-1" />
          Create Game
        </Button>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-1">
        {Array.from({ length: numRounds }, (_, i) => i + 1).map((round) => (
          <RoundColumn
            key={round}
            round={round}
            gamesPerRound={gamesPerRound}
            getSlotGame={(r, g) => getSlotGame(pool.id, r, g)}
            teamDivisions={teamDivisions}
            onEditGame={onEditGame}
            onDragStart={onDragStart}
            hoverSlotId={hoverSlotId}
            poolId={pool.id}
          />
        ))}
      </div>
    </div>
  );
});

const PoolPlayScheduler = React.memo(function PoolPlayScheduler({ divisionGames, divisionTeams, pools, updateGame, refreshGames, onEditGame, onCreateGameForPool, canCreateGame, onEditAssignments, onReassignTeam, roundsPerMatchup }: {
  divisionGames: any[];
  divisionTeams: any[];
  pools: { id: number; name: string; teamIds: string[] }[];
  updateGame: (game: any, cb: any) => Promise<void>;
  refreshGames: () => void;
  roundsPerMatchup: number;
  onEditGame: (game: any) => void;
  onCreateGameForPool: (poolTeams: any[]) => void;
  canCreateGame: boolean;
  onEditAssignments: () => void;
  onReassignTeam: (teamId: string, toPoolId: number) => Promise<void>;
}) {
  const [selectedPoolId, setSelectedPoolId] = useState<number>(() => pools[0]?.id ?? 1);
  const [dragState, setDragState] = useState<{ gameId: string; hoverSlotId: string | null } | null>(null);
  const [sidebarDrag, setSidebarDrag] = useState<{ teamId: string; fromPoolId: number } | null>(null);
  const [sidebarDragOverPoolId, setSidebarDragOverPoolId] = useState<number | null>(null);
  const slotRectsRef = useRef<Map<string, DOMRect>>(new Map());
  const currentOverRef = useRef<string | null>(null);

  useEffect(() => {
    if (pools.length > 0 && !pools.some((p) => p.id === selectedPoolId)) {
      setSelectedPoolId(pools[0].id);
    }
  }, [pools]);

  const assignedGames = useMemo(() => divisionGames.filter((g) => g.competitionMeta != null), [divisionGames]);
  const unassignedGames = useMemo(() => divisionGames.filter((g) => g.competitionMeta == null), [divisionGames]);

  const getSlotGame = useCallback((pool: number, round: number, gameNumber: number) => {
    const slotId = `slot-p${pool}-r${round}-g${gameNumber}`;
    if (dragState) {
      if (dragState.hoverSlotId === slotId) return divisionGames.find((g) => g._id === dragState.gameId);
      const dragged = divisionGames.find((g) => g._id === dragState.gameId);
      if (dragged?.competitionMeta?.pool === pool && dragged?.competitionMeta?.round === round && dragged?.competitionMeta?.gameNumber === gameNumber) return undefined;
    }
    return assignedGames.find((g) => g.competitionMeta.pool === pool && g.competitionMeta.round === round && g.competitionMeta.gameNumber === gameNumber);
  }, [assignedGames, dragState, divisionGames]);

  const displayUnassigned = useMemo(() => {
    if (!dragState) return unassignedGames;
    if (dragState.hoverSlotId === 'unassigned') {
      const isAlreadyUnassigned = unassignedGames.some((g) => g._id === dragState.gameId);
      if (!isAlreadyUnassigned) {
        const dragged = divisionGames.find((g) => g._id === dragState.gameId);
        if (dragged) return [...unassignedGames, dragged];
      }
    }
    return unassignedGames;
  }, [unassignedGames, dragState, divisionGames]);

  const handleDrop = useCallback(async (gameId: string, overId: string) => {
    const game = divisionGames.find((g) => g._id === gameId);
    if (!game) return;
    if (overId === 'unassigned') {
      await updateGame({ ...game, competitionMeta: null }, () => {});
    } else {
      const match = overId.match(/^slot-p(\d+)-r(\d+)-g(\d+)$/);
      if (!match) return;
      const pool = Number(match[1]);
      const round = Number(match[2]);
      const gameNumber = Number(match[3]);
      const occupant = assignedGames.find((g) => g.competitionMeta.pool === pool && g.competitionMeta.round === round && g.competitionMeta.gameNumber === gameNumber);
      if (occupant && occupant._id !== gameId) {
        await updateGame({ ...occupant, competitionMeta: game.competitionMeta ?? null }, () => {});
      }
      await updateGame({ ...game, competitionMeta: { pool, round, gameNumber } }, () => {});
    }
    await refreshGames();
  }, [divisionGames, assignedGames, updateGame, refreshGames]);

  const startDrag = useCallback((e: React.MouseEvent, gameId: string) => {
    e.preventDefault();
    document.body.style.userSelect = 'none';
    slotRectsRef.current.clear();
    document.querySelectorAll('[data-slot-id]').forEach((el) => {
      const id = el.getAttribute('data-slot-id');
      if (id) slotRectsRef.current.set(id, el.getBoundingClientRect());
    });
    currentOverRef.current = null;
    setDragState({ gameId, hoverSlotId: null });
    const onMove = (ev: MouseEvent) => {
      let newOver: string | null = null;
      for (const [id, r] of slotRectsRef.current) {
        if (ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom) {
          newOver = id;
          break;
        }
      }
      if (newOver === currentOverRef.current) return;
      currentOverRef.current = newOver;
      setDragState({ gameId, hoverSlotId: newOver });
    };
    const onUp = async () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      const overId = currentOverRef.current;
      currentOverRef.current = null;
      if (!overId) { setDragState(null); return; }
      try { await handleDrop(gameId, overId); } finally { setDragState(null); }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [handleDrop]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Pool Play Schedule</CardTitle>
            <CardDescription>{pools.length} pools · {divisionTeams.length} total teams</CardDescription>
          </div>
          <Button size="sm" variant="ghost" onClick={onEditAssignments}>
            Edit Assignments
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4">
          {/* Pool sidebar */}
          <div className="w-52 shrink-0 border-r pr-3 space-y-1">
            {pools.map((pool) => {
              const poolTeams = divisionTeams
                .filter((t) => pool.teamIds.includes(t._id))
                .sort((a, b) => (isUnseededTeam(a.seed) ? Infinity : a.seed) - (isUnseededTeam(b.seed) ? Infinity : b.seed));
              const isSelected = selectedPoolId === pool.id;
              const isDragOver = sidebarDragOverPoolId === pool.id;
              return (
                <div
                  key={pool.id}
                  className={`rounded-md border ${isDragOver ? 'border-primary bg-primary/10' : isSelected ? 'border-input bg-muted/50' : 'border-transparent'}`}
                  onDragOver={(e) => { e.preventDefault(); setSidebarDragOverPoolId(pool.id); }}
                  onDragLeave={() => setSidebarDragOverPoolId(null)}
                  onDrop={() => {
                    if (sidebarDrag && sidebarDrag.fromPoolId !== pool.id) {
                      onReassignTeam(sidebarDrag.teamId, pool.id);
                    }
                    setSidebarDrag(null);
                    setSidebarDragOverPoolId(null);
                  }}
                >
                  <div
                    className={`flex items-center justify-between px-2 py-1.5 cursor-pointer rounded-t-md text-sm font-medium ${isSelected ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setSelectedPoolId(pool.id)}
                  >
                    <span>{pool.name}</span>
                    <span className="text-xs text-muted-foreground/60">{poolTeams.length}</span>
                  </div>
                  <div className="px-2 pb-1.5 space-y-0.5">
                    {poolTeams.map((team) => (
                      <div
                        key={team._id}
                        draggable
                        className={`flex items-center gap-1.5 py-0.5 rounded text-xs cursor-grab select-none ${sidebarDrag?.teamId === team._id ? 'opacity-40' : 'hover:bg-muted'}`}
                        onDragStart={() => setSidebarDrag({ teamId: team._id, fromPoolId: pool.id })}
                        onDragEnd={() => { setSidebarDrag(null); setSidebarDragOverPoolId(null); }}
                      >
                        {!isUnseededTeam(team.seed) && (
                          <span className="text-muted-foreground/60 w-5 text-right shrink-0">#{team.seed}</span>
                        )}
                        <span className="truncate text-foreground">{getTeamDisplayName(team)}</span>
                      </div>
                    ))}
                    {poolTeams.length === 0 && (
                      <p className="text-xs text-muted-foreground/60 italic py-0.5">No teams</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Selected pool game schedule */}
          <div className="flex-1 min-w-0">
            {pools.filter((pool) => pool.id === selectedPoolId).map((pool) => {
              const poolTeams = divisionTeams.filter((t) => pool.teamIds.includes(t._id));
              return (
                <PoolSection key={pool.id} pool={pool} poolTeams={poolTeams} getSlotGame={getSlotGame}
                  teamDivisions={divisionTeams} onEditGame={onEditGame} onDragStart={startDrag}
                  hoverSlotId={dragState?.hoverSlotId ?? null}
                  onCreateGame={() => onCreateGameForPool(poolTeams)}
                  canCreateGame={canCreateGame}
                  roundsPerMatchup={roundsPerMatchup} />
              );
            })}
          </div>
        </div>
        <div className="mt-6">
          <p className="text-sm font-medium text-foreground mb-2">
            Unassigned Games {displayUnassigned.length > 0 ? `(${displayUnassigned.length})` : ''}
          </p>
          <UnassignedDropZone games={displayUnassigned} teamDivisions={divisionTeams} onEditGame={onEditGame}
            onDragStart={startDrag} hoverSlotId={dragState?.hoverSlotId ?? null} dragGameId={dragState?.gameId ?? null} />
        </div>
      </CardContent>
    </Card>
  );
});

// Mouse-driven drag between [data-slot-id] drop zones, shared by the schedulers.
function useSlotDrag(handleDrop: (gameId: string, overId: string) => Promise<void>) {
  const [dragState, setDragState] = useState<{ gameId: string; hoverSlotId: string | null } | null>(null);
  const slotRectsRef = useRef<Map<string, DOMRect>>(new Map());
  const currentOverRef = useRef<string | null>(null);

  const startDrag = useCallback((e: React.MouseEvent, gameId: string) => {
    e.preventDefault();
    document.body.style.userSelect = 'none';

    slotRectsRef.current.clear();
    document.querySelectorAll('[data-slot-id]').forEach((el) => {
      const id = el.getAttribute('data-slot-id');
      if (id) slotRectsRef.current.set(id, el.getBoundingClientRect());
    });

    currentOverRef.current = null;
    setDragState({ gameId, hoverSlotId: null });

    const onMove = (ev: MouseEvent) => {
      let newOver: string | null = null;
      for (const [id, r] of slotRectsRef.current) {
        if (ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom) {
          newOver = id;
          break;
        }
      }
      if (newOver === currentOverRef.current) return;
      currentOverRef.current = newOver;
      setDragState({ gameId, hoverSlotId: newOver });
    };

    const onUp = async () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';

      const overId = currentOverRef.current;
      currentOverRef.current = null;
      if (!overId) {
        setDragState(null);
        return;
      }
      try {
        await handleDrop(gameId, overId);
      } finally {
        setDragState(null);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [handleDrop]);

  return { dragState, startDrag };
}

// Canonical playoff slot key for a game — new games carry `bracketSlot`; legacy
// games have only `stage` (championship/third_place), so fall back to it.
const playoffKeyOf = (meta: any) => meta?.bracketSlot ?? meta?.stage;

// Fields that tie a game to a playoff slot — kept in sync so the public
// Playoff Results view (which groups on top-level `playoffRound`) and the
// API's bracket advancement both see scheduler assignments. Pass `slot: null`
// for a game leaving the playoff bracket (RR slot or unassigned).
const playoffAssignmentFields = (slots: PlayoffSlot[], slot: PlayoffSlot | null) =>
  slot
    ? {
        playoffRound: slot.playoffRound,
        gameLabel: slot.label,
        homeTeamPlaceholder: placeholderFromSlots(slots, slot.key, 'home') || '',
        awayTeamPlaceholder: placeholderFromSlots(slots, slot.key, 'away') || '',
      }
    : {
        playoffRound: null,
        gameLabel: '',
        homeTeamPlaceholder: '',
        awayTeamPlaceholder: '',
      };

const PlayoffColumn = React.memo(function PlayoffColumn({ slots, getPlayoffGame, teamDivisions, onEditGame, hoverSlotId, onDragStart, dragGameId }: {
  slots: { key: string; label: string; stage: string; group: string }[];
  getPlayoffGame: (slotKey: string) => any;
  teamDivisions: any[];
  onEditGame: (g: any) => void;
  hoverSlotId: string | null;
  onDragStart: (e: React.MouseEvent, gameId: string) => void;
  dragGameId: string | null;
}) {
  // Group slots into their visual bracket columns (Quarterfinals → Semifinals → Final → 3rd Place).
  const groups: { group: string; slots: typeof slots }[] = [];
  slots.forEach((slot) => {
    const last = groups[groups.length - 1];
    if (last && last.group === slot.group) last.slots.push(slot);
    else groups.push({ group: slot.group, slots: [slot] });
  });
  return (
    <div className="flex-shrink-0 flex gap-4">
      {groups.map(({ group, slots: groupSlots }) => (
        <div key={group} className="flex-shrink-0 w-52 space-y-2">
          <div className="text-xs font-semibold text-center text-purple-600 pb-1 border-b border-purple-200 uppercase tracking-wide">{group}</div>
          {groupSlots.map((slot) => {
            const slotId = `slot-playoff-${slot.key}`;
            const game = getPlayoffGame(slot.key);
            const isDragPreview = hoverSlotId === slotId;
            return (
              <div key={slot.key}>
                <div className="text-xs text-muted-foreground/60 mb-0.5">{slot.label}</div>
                <GameSlot slotId={slotId} game={game ?? null} teamDivisions={teamDivisions} onEditGame={onEditGame} onDragStart={onDragStart} isDragPreview={isDragPreview} />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
});

// ─── Round Robin Scheduler ────────────────────────────────────────────────────
const RoundRobinScheduler = React.memo(function RoundRobinScheduler({
  divisionGames,
  divisionTeams,
  numTeams,
  numRounds,
  gamesPerRound,
  isEvenTeams,
  isPlayoffs,
  playoffTeams,
  updateGame,
  refreshGames,
  onEditGame,
}: {
  divisionGames: any[];
  divisionTeams: any[];
  numTeams: number;
  numRounds: number;
  gamesPerRound: number;
  isEvenTeams: boolean;
  isPlayoffs: boolean;
  playoffTeams: number;
  updateGame: (game: any, cb: any) => Promise<void>;
  refreshGames: () => void;
  onEditGame: (game: any) => void;
}) {
  const playoffSlots = useMemo(() => getPlayoffSlots(playoffTeams), [playoffTeams]);

  const assignedGames = useMemo(() => divisionGames.filter((g) => g.competitionMeta != null), [divisionGames]);
  const unassignedGames = useMemo(() => divisionGames.filter((g) => g.competitionMeta == null), [divisionGames]);

  // The backend deep-merges competitionMeta, so an assignment must explicitly
  // null the other shape's keys or a game moved between an RR slot and a
  // playoff slot keeps stale round/bracketSlot values.
  const clearIfPlayoff = useCallback(
    (g: any) => (playoffKeyOf(g?.competitionMeta) || g?.playoffRound ? playoffAssignmentFields(playoffSlots, null) : {}),
    [playoffSlots],
  );

  // Full field set for placing `g` at the given competitionMeta (used when a
  // slot's occupant inherits the dragged game's old assignment).
  const assignmentFor = useCallback((g: any, meta: any) => {
    const key = playoffKeyOf(meta);
    const slot = key ? playoffSlots.find((s) => s.key === key) ?? null : null;
    if (slot) {
      return {
        competitionMeta: { stage: slot.stage, bracketSlot: slot.key, round: null, gameNumber: null, pool: null },
        ...playoffAssignmentFields(playoffSlots, slot),
      };
    }
    if (meta && (meta.round != null || meta.gameNumber != null)) {
      return {
        competitionMeta: { round: meta.round, gameNumber: meta.gameNumber, ...(meta.pool != null ? { pool: meta.pool } : { pool: null }), stage: null, bracketSlot: null },
        ...clearIfPlayoff(g),
      };
    }
    return { competitionMeta: null, ...clearIfPlayoff(g) };
  }, [playoffSlots, clearIfPlayoff]);

  const handleDrop = useCallback(async (gameId: string, overId: string) => {
    const game = divisionGames.find((g) => g._id === gameId);
    if (!game) return;

    if (overId === 'unassigned') {
      await updateGame({ ...game, ...assignmentFor(game, null) }, () => {});
    } else if (overId.startsWith('slot-playoff-')) {
      const slotKey = overId.slice('slot-playoff-'.length);
      const slot = playoffSlots.find((s) => s.key === slotKey);
      if (!slot) return;
      const occupant = assignedGames.find((g) => g.competitionMeta && playoffKeyOf(g.competitionMeta) === slotKey);
      if (occupant && occupant._id !== gameId) {
        await updateGame({ ...occupant, ...assignmentFor(occupant, game.competitionMeta) }, () => {});
      }
      await updateGame({ ...game, ...assignmentFor(game, { bracketSlot: slotKey }) }, () => {});
    } else {
      const match = overId.match(/^slot-r(\d+)-g(\d+)$/);
      if (!match) return;
      const round = Number(match[1]);
      const gameNumber = Number(match[2]);
      const occupant = assignedGames.find(
        (g) => g.competitionMeta.round === round && g.competitionMeta.gameNumber === gameNumber && !g.competitionMeta.stage,
      );
      if (occupant && occupant._id !== gameId) {
        await updateGame({ ...occupant, ...assignmentFor(occupant, game.competitionMeta) }, () => {});
      }
      await updateGame({ ...game, ...assignmentFor(game, { round, gameNumber }) }, () => {});
    }
    await refreshGames();
  }, [divisionGames, assignedGames, updateGame, refreshGames, playoffSlots, assignmentFor]);

  const { dragState, startDrag } = useSlotDrag(handleDrop);

  const getSlotGame = useCallback(
    (round: number, gameNumber: number) => {
      const slotId = `slot-r${round}-g${gameNumber}`;
      if (dragState) {
        if (dragState.hoverSlotId === slotId) {
          return divisionGames.find((g) => g._id === dragState.gameId);
        }
        const draggedGame = divisionGames.find((g) => g._id === dragState.gameId);
        if (
          draggedGame?.competitionMeta?.round === round &&
          draggedGame?.competitionMeta?.gameNumber === gameNumber &&
          !draggedGame?.competitionMeta?.stage
        ) {
          return undefined;
        }
      }
      return assignedGames.find(
        (g) => g.competitionMeta.round === round && g.competitionMeta.gameNumber === gameNumber && !g.competitionMeta.stage,
      );
    },
    [assignedGames, dragState, divisionGames],
  );

  const getPlayoffGame = useCallback(
    (slotKey: string) => {
      const slotId = `slot-playoff-${slotKey}`;
      if (dragState) {
        if (dragState.hoverSlotId === slotId) {
          return divisionGames.find((g) => g._id === dragState.gameId);
        }
        const draggedGame = divisionGames.find((g) => g._id === dragState.gameId);
        if (draggedGame && playoffKeyOf(draggedGame.competitionMeta) === slotKey) return undefined;
      }
      return assignedGames.find((g) => g.competitionMeta && playoffKeyOf(g.competitionMeta) === slotKey);
    },
    [assignedGames, dragState, divisionGames],
  );

  const displayUnassigned = useMemo(() => {
    if (!dragState) return unassignedGames;
    // If hovering unassigned zone and dragged game is assigned (not in the unassigned list), preview it there
    if (dragState.hoverSlotId === 'unassigned') {
      const isAlreadyUnassigned = unassignedGames.some((g) => g._id === dragState.gameId);
      if (!isAlreadyUnassigned) {
        const draggedGame = divisionGames.find((g) => g._id === dragState.gameId);
        if (draggedGame) return [...unassignedGames, draggedGame];
      }
    }
    return unassignedGames;
  }, [unassignedGames, dragState, divisionGames]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{isPlayoffs ? 'Round Robin with Playoffs Schedule' : 'Round Robin Schedule'}</CardTitle>
        <CardDescription>
          {numTeams} teams · {numRounds} round{numRounds !== 1 ? 's' : ''} · {gamesPerRound} game{gamesPerRound !== 1 ? 's' : ''} per round
          {!isEvenTeams && ' (1 bye per round)'}
          {isPlayoffs && ` · ${playoffTeams}-team playoff bracket`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4 overflow-x-auto pb-2">
          {Array.from({ length: numRounds }, (_, i) => i + 1).map((round) => (
            <RoundColumn
              key={round}
              round={round}
              gamesPerRound={gamesPerRound}
              getSlotGame={getSlotGame}
              teamDivisions={divisionTeams}
              onEditGame={onEditGame}
              onDragStart={startDrag}
              hoverSlotId={dragState?.hoverSlotId ?? null}
            />
          ))}
          {isPlayoffs && (
            <PlayoffColumn
              slots={playoffSlots}
              getPlayoffGame={getPlayoffGame}
              teamDivisions={divisionTeams}
              onEditGame={onEditGame}
              hoverSlotId={dragState?.hoverSlotId ?? null}
              onDragStart={startDrag}
              dragGameId={dragState?.gameId ?? null}
            />
          )}
        </div>

        <div className="mt-4">
          <p className="text-sm font-medium text-foreground mb-2">
            Unassigned Games {displayUnassigned.length > 0 ? `(${displayUnassigned.length})` : ''}
          </p>
          <UnassignedDropZone games={displayUnassigned} teamDivisions={divisionTeams} onEditGame={onEditGame} onDragStart={startDrag} hoverSlotId={dragState?.hoverSlotId ?? null} dragGameId={dragState?.gameId ?? null} />
        </div>
      </CardContent>
    </Card>
  );
});

// ─── Bracket Scheduler (standalone playoffs / bracket / consolation) ─────────
const BracketScheduler = React.memo(function BracketScheduler({
  divisionGames,
  divisionTeams,
  format,
  playoffTeams,
  updateGame,
  refreshGames,
  onEditGame,
}: {
  divisionGames: any[];
  divisionTeams: any[];
  format: string;
  playoffTeams: number;
  updateGame: (game: any, cb: any) => Promise<void>;
  refreshGames: () => void;
  onEditGame: (game: any) => void;
}) {
  const slots = useMemo(() => getPlayoffSlotsForFormat(format, playoffTeams), [format, playoffTeams]);

  const assignedGames = useMemo(() => divisionGames.filter((g) => g.competitionMeta != null), [divisionGames]);
  const unassignedGames = useMemo(() => divisionGames.filter((g) => g.competitionMeta == null), [divisionGames]);

  const handleDrop = useCallback(async (gameId: string, overId: string) => {
    const game = divisionGames.find((g) => g._id === gameId);
    if (!game) return;

    if (overId === 'unassigned') {
      await updateGame({ ...game, competitionMeta: null, ...playoffAssignmentFields(slots, null) }, () => {});
    } else if (overId.startsWith('slot-playoff-')) {
      const slotKey = overId.slice('slot-playoff-'.length);
      const slot = slots.find((s) => s.key === slotKey);
      if (!slot) return;
      const occupant = assignedGames.find((g) => g.competitionMeta && playoffKeyOf(g.competitionMeta) === slotKey);
      if (occupant && occupant._id !== gameId) {
        const oldKey = playoffKeyOf(game.competitionMeta);
        const oldSlot = oldKey ? slots.find((s) => s.key === oldKey) ?? null : null;
        await updateGame(
          oldSlot
            ? { ...occupant, competitionMeta: { stage: oldSlot.stage, bracketSlot: oldSlot.key }, ...playoffAssignmentFields(slots, oldSlot) }
            : { ...occupant, competitionMeta: null, ...playoffAssignmentFields(slots, null) },
          () => {},
        );
      }
      await updateGame(
        { ...game, competitionMeta: { stage: slot.stage, bracketSlot: slot.key }, ...playoffAssignmentFields(slots, slot) },
        () => {},
      );
    }
    await refreshGames();
  }, [divisionGames, assignedGames, updateGame, refreshGames, slots]);

  const { dragState, startDrag } = useSlotDrag(handleDrop);

  const getPlayoffGame = useCallback(
    (slotKey: string) => {
      const slotId = `slot-playoff-${slotKey}`;
      if (dragState) {
        if (dragState.hoverSlotId === slotId) {
          return divisionGames.find((g) => g._id === dragState.gameId);
        }
        const draggedGame = divisionGames.find((g) => g._id === dragState.gameId);
        if (draggedGame && playoffKeyOf(draggedGame.competitionMeta) === slotKey) return undefined;
      }
      return assignedGames.find((g) => g.competitionMeta && playoffKeyOf(g.competitionMeta) === slotKey);
    },
    [assignedGames, dragState, divisionGames],
  );

  const displayUnassigned = useMemo(() => {
    if (!dragState) return unassignedGames;
    if (dragState.hoverSlotId === 'unassigned') {
      const isAlreadyUnassigned = unassignedGames.some((g) => g._id === dragState.gameId);
      if (!isAlreadyUnassigned) {
        const draggedGame = divisionGames.find((g) => g._id === dragState.gameId);
        if (draggedGame) return [...unassignedGames, draggedGame];
      }
    }
    return unassignedGames;
  }, [unassignedGames, dragState, divisionGames]);

  const isConsolation = isConsolationFormat(format);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{isConsolation ? 'Playoff Bracket with Consolation' : 'Playoff Bracket'}</CardTitle>
        <CardDescription>
          {playoffTeams}-team bracket
          {isConsolation && ' · every team plays for a final placement'}
          {' · winners advance automatically as scores are entered'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* INT-0037: same two-sided tree shape as the public PlayoffBracket
            (shared buildBracketLayout geometry) — slots keep the exact
            slot-playoff-<key> ids, so useSlotDrag/handleDrop (ADM-05-10)
            are untouched; only the arrangement changed. */}
        <BracketTree
          slots={slots as any}
          renderSlot={(slot: any) => {
            const slotId = `slot-playoff-${slot.key}`;
            return (
              <div>
                <div className="text-xs text-muted-foreground/60 mb-0.5">{slot.label}</div>
                <GameSlot
                  slotId={slotId}
                  game={getPlayoffGame(slot.key) ?? null}
                  teamDivisions={divisionTeams}
                  onEditGame={onEditGame}
                  onDragStart={startDrag}
                  isDragPreview={(dragState?.hoverSlotId ?? null) === slotId}
                />
              </div>
            );
          }}
        />

        <div className="mt-4">
          <p className="text-sm font-medium text-foreground mb-2">
            Unassigned Games {displayUnassigned.length > 0 ? `(${displayUnassigned.length})` : ''}
          </p>
          <UnassignedDropZone games={displayUnassigned} teamDivisions={divisionTeams} onEditGame={onEditGame} onDragStart={startDrag} hoverSlotId={dragState?.hoverSlotId ?? null} dragGameId={dragState?.gameId ?? null} />
        </div>
      </CardContent>
    </Card>
  );
});

// ─── Sortable team row ────────────────────────────────────────────────────────
function SortableTeamRow({ team, seed }: { team: any; seed: number }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: team._id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="flex items-center gap-3 border rounded px-3 py-2 bg-card"
    >
      <span className="text-xs text-muted-foreground/60 w-5 text-right shrink-0">{seed}</span>
      <GripVertical className="h-4 w-4 text-muted-foreground/60 cursor-grab shrink-0" {...attributes} {...listeners} />
      <span className="text-sm font-medium">{getTeamDisplayName(team)}</span>
      {team.registrationTeam ? (
        <span className="text-xs bg-success/10 text-success px-2 py-0.5 rounded-full ml-auto">Registered</span>
      ) : (
        <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full ml-auto">Unregistered</span>
      )}
    </div>
  );
}

// ─── Team List ────────────────────────────────────────────────────────────────
function TeamList({
  teams,
  pendingTeamIds,
  divisionGames,
  onAddTeam,
  isSeedSortMode,
  seedOrder,
  onEnterSeedSort,
  onDragEnd,
  isSeedDirty,
  isSavingSeeds,
  onSaveSeeds,
  onCancelSeedSort,
}: {
  teams: any[];
  pendingTeamIds?: Set<string>;
  divisionGames: any[];
  onAddTeam: () => void;
  isSeedSortMode: boolean;
  seedOrder: string[];
  onEnterSeedSort: () => void;
  onDragEnd: (event: DragEndEvent) => void;
  isSeedDirty: boolean;
  isSavingSeeds: boolean;
  onSaveSeeds: () => void;
  onCancelSeedSort: () => void;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const orderedTeams = isSeedSortMode
    ? seedOrder.map((id) => teams.find((t) => t._id === id)).filter(Boolean)
    : teams;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Teams {teams.length > 0 ? `(${teams.length})` : ''}</CardTitle>
        <div className="flex items-center gap-2">
          {isSeedSortMode ? (
            <>
              <Button size="sm" variant="outline" onClick={onCancelSeedSort} disabled={isSavingSeeds}>
                Cancel
              </Button>
              {isSeedDirty && (
                <Button size="sm" onClick={onSaveSeeds} disabled={isSavingSeeds}>
                  {isSavingSeeds ? 'Saving…' : 'Save'}
                </Button>
              )}
            </>
          ) : (
            <>
              {teams.length > 1 && (
                <Button size="sm" variant="outline" onClick={onEnterSeedSort}>
                  Sort by Seed
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={onAddTeam}>
                <Plus className="h-3 w-3 mr-1" />
                Add Team
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {teams.length === 0 ? (
          <p className="text-sm text-muted-foreground">No teams in this division.</p>
        ) : isSeedSortMode ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={seedOrder} strategy={verticalListSortingStrategy}>
              <div className="space-y-1">
                {orderedTeams.map((team, idx) => (
                  <SortableTeamRow key={team._id} team={team} seed={idx + 1} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="flex flex-wrap gap-2">
            {[...teams].sort((a, b) => (isUnseededTeam(a.seed) ? Infinity : a.seed) - (isUnseededTeam(b.seed) ? Infinity : b.seed)).map((team) => (
              <div key={team._id} className="flex items-center gap-2 border rounded px-3 py-2 bg-card">
                <span className="text-sm font-medium">
                  {!isUnseededTeam(team.seed) ? `#${team.seed} ` : ''}{getTeamDisplayName(team)} ({divisionGames.filter((g) => helpers.getObjId(g.homeTeam) === team._id || helpers.getObjId(g.awayTeam) === team._id).length})
                </span>
                {team.registrationTeam ? (
                  <span className="text-xs bg-success/10 text-success px-2 py-0.5 rounded-full">Registered</span>
                ) : (
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">Unregistered</span>
                )}
                {pendingTeamIds?.has(String(team._id)) && (
                  <span className="text-xs bg-warning/10 text-warning px-2 py-0.5 rounded-full">Pending payment</span>
                )}
                <Link to={`/admin/team/${team._id}`} className="text-muted-foreground/60 hover:text-foreground">
                  <Pencil className="h-3.5 w-3.5" />
                </Link>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function DivisionPage() {
  const { divisionId } = useParams();
  // Org slug from OrganizationLayout's outlet context. Locations/time slots are
  // organization-scoped (matched on Organization.name), so fetch by org name —
  // NOT the season/contest `league.name`.
  // `league` from the outlet context is the Organization doc (see OrganizationLayout);
  // sport is single-sourced from organization.sports[0] — the division.sports mirror
  // is unreliable/empty on older divisions.
  const { leagueName: orgName = '', league: organization } = (useOutletContext() as any) || {};
  const { getToken } = useAuth();
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const scheduleMeasureRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0); // 0 = not yet measured

  const [selectedDateKey, setSelectedDateKey] = useState('');
  // Manual override for where the Game Schedule grid starts (minutes since
  // midnight). null = Auto (1 hour before the first game). Session-only —
  // resets to Auto when the selected date changes.
  const [gridStartOverride, setGridStartOverride] = useState<number | null>(null);
  // Schedule grid ⇄ score table toggle (owner 2026-10-09): the table view is
  // a spreadsheet of all games for fast bulk score entry.
  const [viewMode, setViewMode] = useState<'schedule' | 'table' | 'standings'>('schedule');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // INT-0026 — AI schedule import dialog.
  const [isScheduleUploadOpen, setIsScheduleUploadOpen] = useState(false);
  const [autoPopulateOpen, setAutoPopulateOpen] = useState(false);
  const [isAutoPopulating, setIsAutoPopulating] = useState(false);
  const [playoffPopulateOpen, setPlayoffPopulateOpen] = useState(false);
  const [isPlayoffPopulating, setIsPlayoffPopulating] = useState(false);
  const [createFormPoolTeams, setCreateFormPoolTeams] = useState<any[]>([]);
  const [isAddTeamOpen, setIsAddTeamOpen] = useState(false);
  const [isRegistrationLoading, setIsRegistrationLoading] = useState(false);
  const [registration, setRegistration] = useState<any>(null);
  const [addTeamForm, setAddTeamForm] = useState<{
    teamName: string;
    players: { info: Record<string, any> }[];
    sendInvite: boolean;
    inviteEmail: string;
    inviteFirstName: string;
    inviteLastName: string;
    forcePlace: boolean;
    ignoreDeadline: boolean;
  }>({ teamName: '', players: [], sendInvite: false, inviteEmail: '', inviteFirstName: '', inviteLastName: '', forcePlace: false, ignoreDeadline: false });
  // Confirmation for the "Force team onto division" option (shown when toggled ON).
  const [forceConfirmOpen, setForceConfirmOpen] = useState(false);
  const [divisionFormat, setDivisionFormat] = useState('');
  const [isSavingFormat, setIsSavingFormat] = useState(false);
  // Basketball scoresheet preview modal: one built jsPDF doc + blob-URL preview
  // per variant. Clicking a preview downloads that variant's sheet.
  const [scoresheetOpen, setScoresheetOpen] = useState(false);
  const [scoresheetPreviews, setScoresheetPreviews] = useState<
    { variantId: string; label: string; url: string; filename: string; doc: any }[]
  >([]);
  const closeScoresheetPreviews = () => {
    setScoresheetPreviews((prev) => {
      prev.forEach((p) => { if (p.url) { try { URL.revokeObjectURL(p.url); } catch (e) { /* noop */ } } });
      return [];
    });
    setScoresheetOpen(false);
  };
  const openScoresheetPreviews = () => {
    try {
      const sport = organization?.sports?.[0] || 'basketball';
      const scoresheetData = {
        division,
        sport,
        statKeys: (effectiveConfig as any)?.gameStats?.[sport],
        maxPlayers: (effectiveConfig as any)?.maxPlayers,
        leagueName,
      };
      const previews = BASKETBALL_SCORESHEET_VARIANTS.map((v) => {
        const doc = buildBasketballScoresheetDoc(v.id, scoresheetData);
        return {
          variantId: v.id,
          label: v.label,
          url: doc.output('bloburl') as unknown as string,
          filename: basketballScoresheetFilename(v.id, scoresheetData),
          doc,
        };
      });
      closeScoresheetPreviews();
      setScoresheetPreviews(previews);
      setScoresheetOpen(true);
    } catch (err) {
      console.error('basketball scoresheet generation failed', err);
      toast.error('Could not generate scoresheet');
    }
  };
  const [pendingFormat, setPendingFormat] = useState<string | null>(null);
  const [poolSetupOpen, setPoolSetupOpen] = useState(false);
  const [poolSetupFormat, setPoolSetupFormat] = useState('');
  const [poolSetupCount, setPoolSetupCount] = useState<number | ''>(2);
  const [poolSetupRoundsPerMatchup, setPoolSetupRoundsPerMatchup] = useState<number | ''>(1);
  const [poolSetupPlayoffTeams, setPoolSetupPlayoffTeams] = useState<number>(4);
  const [roundRobinSetupOpen, setRoundRobinSetupOpen] = useState(false);
  const [roundRobinSetupFormat, setRoundRobinSetupFormat] = useState('');
  const [roundRobinSetupCount, setRoundRobinSetupCount] = useState<number | ''>(1);
  const [roundRobinSetupPlayoffTeams, setRoundRobinSetupPlayoffTeams] = useState<number>(4);
  const [editPoolsOpen, setEditPoolsOpen] = useState(false);
  const [editPoolAssignments, setEditPoolAssignments] = useState<Record<string, number>>({});
  const [isSavingPoolAssignments, setIsSavingPoolAssignments] = useState(false);
  const [editGameOpen, setEditGameOpen] = useState(false);
  const [editGameTarget, setEditGameTarget] = useState<any>(null);
  // Scoresheet scan dialog state — { type: 'basketball' | 'pickleball', game? }.
  // Mirrors UpcomingGamesAdmin's `scan` minus divisionId (division already loaded here).
  const [scan, setScan] = useState<any>(null);
  const [editGameForm, setEditGameForm] = useState<{ homeTeamId: string; awayTeamId: string; locationId: string; dateKey: string; startMinutes: number; duration: number; homeScore: number | ''; awayScore: number | '' }>({ homeTeamId: '', awayTeamId: '', locationId: '', dateKey: '', startMinutes: 9 * 60, duration: DEFAULT_DURATION, homeScore: '', awayScore: '' });

  const [isSeedSortMode, setIsSeedSortMode] = useState(false);
  const [seedOrder, setSeedOrder] = useState<string[]>([]);
  const [isSeedDirty, setIsSeedDirty] = useState(false);
  const [isSavingSeeds, setIsSavingSeeds] = useState(false);

  const [draftByGameId, setDraftByGameId] = useState<Record<string, { start: number; duration: number; locationId?: string }>>({});
  const [interaction, setInteraction] = useState<any>(null);
  const draftByGameIdRef = useRef<Record<string, { start: number; duration: number; locationId?: string }>>({});

  const [createForm, setCreateForm] = useState({
    homeTeamId: '',
    awayTeamId: '',
    locationId: '',
    dateKey: '',
    startMinutes: 9 * 60,
    duration: DEFAULT_DURATION,
    homeScore: '' as number | '',
    awayScore: '' as number | '',
  });

  // ─── Store access ───────────────────────────────────────────────────────────
  const { divisions, getDivisionById, updateDivision } = useDivisionStore((state: any) => ({
    divisions: state.divisions,
    getDivisionById: state.getDivisionById,
    updateDivision: state.updateDivision,
  }));
  const { teamDivisions, getTeamDivisionsByDivisionId, divisionStats, getDivisionStats } = useTeamDivisionStore((state: any) => ({
    teamDivisions: state.teamDivisions,
    getTeamDivisionsByDivisionId: state.getTeamDivisionsByDivisionId,
    divisionStats: state.divisionStats,
    getDivisionStats: state.getDivisionStats,
  }));
  const { games, getGamesByDivisionId } = useGameStore((state: any) => ({
    games: state.games,
    getGamesByDivisionId: state.getGamesByDivisionId,
  }));
  const { leagues, getLeagueById } = useLeagueStore((state: any) => ({
    leagues: state.leagues,
    getLeagueById: state.getLeagueById,
  }));
  const { timeSlots, getTimeSlotsByLeagueName } = useTimeSlotStore((state: any) => ({
    timeSlots: state.timeSlots,
    getTimeSlotsByLeagueName: state.getTimeSlotsByLeagueName,
  }));
  const { allLocations, getLocationsByLeagueName, createLocation } = useLocationStore((state: any) => ({
    createLocation: state.createLocation(getToken),
    allLocations: state.locations,
    getLocationsByLeagueName: state.getLocationsByLeagueName,
  }));
  const addGameFn = useGameStore((state: any) => state.addGame);
  const updateGameFn = useGameStore((state: any) => state.updateGame);
  const addBulkGamesFn = useGameStore((state: any) => state.addBulkGames);
  const deleteGameFn = useGameStore((state: any) => state.deleteGame);
  const addGame = useMemo(() => addGameFn(getToken), [addGameFn, getToken]);
  const updateGame = useMemo(() => updateGameFn(getToken), [updateGameFn, getToken]);
  const addBulkGames = useMemo(() => addBulkGamesFn(getToken), [addBulkGamesFn, getToken]);
  const deleteGame = useMemo(() => deleteGameFn(getToken), [deleteGameFn, getToken]);
  const addTeamDivisionFn = useTeamDivisionStore((state: any) => state.addTeamDivision);
  const addTeamDivision = useMemo(() => addTeamDivisionFn(getToken), [addTeamDivisionFn, getToken]);
  const inviteManagerFn = useTeamDivisionStore((state: any) => state.inviteManager);
  const inviteManager = useMemo(() => inviteManagerFn(getToken), [inviteManagerFn, getToken]);
  const updateTeamDivisionFn = useTeamDivisionStore((state: any) => state.updateTeamDivision);
  const updateTeamDivision = useMemo(() => updateTeamDivisionFn(getToken), [updateTeamDivisionFn, getToken]);
  const { getRegistrationByLeagueId, getRegistrationByTournamentId } = useRegistrationStore((state: any) => ({
    getRegistrationByLeagueId: state.getRegistrationByLeagueId,
    getRegistrationByTournamentId: state.getRegistrationByTournamentId,
  }));
  const { tournaments, getTournamentById } = useTournamentStore((state: any) => ({
    tournaments: state.tournaments,
    getTournamentById: state.getTournamentById,
  }));

  // ─── Derived data ───────────────────────────────────────────────────────────
  const division = useMemo(() => _.find(divisions, { _id: divisionId }), [divisions, divisionId]);
  // Sport-aware default game length (pickleball = 30, else 60). Sport is
  // single-sourced from organization.sports[0].
  const defaultDuration = useMemo(() => getDefaultGameDuration(organization?.sports?.[0]), [organization]);
  const sport = organization?.sports?.[0];
  const scanSupported = isScoresheetSupportedSport(sport);
  const leagueId = division ? helpers.getObjId(division.league) : null;
  const league = useMemo(() => _.find(leagues, { _id: leagueId }), [leagues, leagueId]);
  const leagueName = league?.name;
  const isTournamentDivision = !!league?.tournament;
  const tournamentId = league ? helpers.getObjId(league.tournament) : null;
  const tournament = useMemo(
    () => (tournamentId ? _.find(tournaments, { _id: tournamentId }) : null),
    [tournaments, tournamentId],
  );

  // Only "qualified" teams (holding a capacity spot: secured/waitlisted) are shown
  // and used across the page — unpaid `pending` teams are hidden, matching
  // /admin/organize. Filtering at the source propagates to the roster, schedulers,
  // seeding, game-creation dropdowns, and auto-populate.
  // Division page = full management surface: show ALL the division's teams
  // (unlike /admin/organize, which hides capacity-`pending` teams). Unpaid
  // (pending) teams are badged "Pending payment" instead of hidden — otherwise
  // an admin-added team (now always linked to a RegistrationTeam) would vanish
  // from this list until paid/accepted.
  const divisionTeamStates = useMemo(
    () => {
      const forDivision = teamDivisions.filter((t) => helpers.getObjId(t.division) === divisionId);
      return deriveDivisionTeamStates(forDivision, divisionStats, divisionId);
    },
    [teamDivisions, divisionId, divisionStats],
  );
  const divisionTeams = useMemo(() => divisionTeamStates.map((x) => x.team), [divisionTeamStates]);
  const pendingTeamIds = useMemo(
    () => new Set(
      divisionTeamStates
        .filter((x) => x.state === 'pending' && !(x.rt && x.rt.status === 'accepted'))
        .map((x) => String(x.team._id)),
    ),
    [divisionTeamStates],
  );

  // ─── Sync format from division ───────────────────────────────────────────────
  useEffect(() => {
    if (division) setDivisionFormat(division.config?.format || '');
  }, [division?._id]);

  // ─── Data loading ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!divisionId) return;
    getDivisionById(divisionId);
    getTeamDivisionsByDivisionId(divisionId);
    getDivisionStats(divisionId);
  }, [divisionId]);

  useEffect(() => {
    if (!leagueId) return;
    getLeagueById(leagueId, () => {});
  }, [leagueId]);

  useEffect(() => {
    if (!orgName) return;
    getTimeSlotsByLeagueName(orgName);
    getLocationsByLeagueName(orgName, { includeSubVenues: true });
    getGamesByDivisionId(divisionId);
  }, [orgName, divisionId]);

  // ─── Date keys ──────────────────────────────────────────────────────────────
  const allDateKeys = useMemo(() => {
    const dateKeys = new Set<string>();
    timeSlots.forEach((slot) => {
      const key = toDateKey(slot.availableDate);
      if (key) dateKeys.add(key);
    });
    games.forEach((game) => {
      const key = toDateKey(game.globalDate || game.date);
      if (key) dateKeys.add(key);
    });
    return Array.from(dateKeys).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  }, [timeSlots, games]);

  useEffect(() => {
    if (!selectedDateKey && allDateKeys.length > 0) {
      setSelectedDateKey(allDateKeys[0]);
    }
  }, [allDateKeys, selectedDateKey]);

  // ─── Location resolution ─────────────────────────────────────────────────
  const locationMatchList = useMemo(() => {
    if (allLocations.length > 0) return allLocations;
    const map = new Map<string, any>();
    timeSlots.forEach((slot) => {
      const locationId = helpers.getObjId(slot.location);
      if (locationId && slot.location) map.set(locationId, slot.location);
    });
    return Array.from(map.values());
  }, [allLocations, timeSlots]);

  // Bookable locations for the game modals: sub-venues (courts) + standalone
  // venues. A parent venue that has children isn't itself bookable, so hide it.
  const selectableLocations = useMemo(() => {
    const hasChildren = new Set<string>();
    locationMatchList.forEach((l) => {
      const p = helpers.getObjId(l.parentLocation);
      if (p) hasChildren.add(p);
    });
    return locationMatchList.filter((l) =>
      helpers.getObjId(l.parentLocation) || !hasChildren.has(helpers.getObjId(l))
    );
  }, [locationMatchList]);

  const locations = useMemo(() => {
    const map = new Map<string, any>();
    const byId = new Map<string, any>();
    const subVenuesByParent = new Map<string, any[]>();

    locationMatchList.forEach((loc) => {
      const id = helpers.getObjId(loc);
      if (!id) return;
      byId.set(id, loc);
      const parentId = helpers.getObjId(loc.parentLocation);
      if (parentId) {
        if (!subVenuesByParent.has(parentId)) subVenuesByParent.set(parentId, []);
        subVenuesByParent.get(parentId).push(loc);
      }
    });

    const usedParentIds = new Set<string>();
    const parentHasGames = new Set<string>();

    timeSlots.forEach((slot) => {
      const slotDateKey = toDateKey(slot.availableDate);
      if (slotDateKey !== selectedDateKey) return;
      const locationId = helpers.getObjId(slot.location);
      if (!locationId) return;
      const loc = byId.get(locationId) || slot.location;
      const parentId = helpers.getObjId(loc?.parentLocation);
      if (parentId) {
        map.set(locationId, loc);
      } else {
        usedParentIds.add(locationId);
      }
    });

    games.forEach((game) => {
      const gameDateKey = toDateKey(game.globalDate || game.date);
      if (gameDateKey !== selectedDateKey) return;
      const modelId = getLocationIdFromGame(game, locationMatchList);
      if (modelId) {
        const existing = map.get(modelId);
        if (!existing) {
          const loc = byId.get(modelId);
          if (loc) {
            const parentId = helpers.getObjId(loc.parentLocation);
            if (parentId) {
              map.set(modelId, loc);
            } else {
              usedParentIds.add(modelId);
              parentHasGames.add(modelId);
            }
          } else {
            map.set(modelId, { _id: modelId, name: game.location || 'Location' });
          }
        }
      } else if (typeof game.location === 'string') {
        const key = `name:${game.location}`;
        if (!map.has(key)) map.set(key, { _id: key, name: game.location });
      }
    });

    usedParentIds.forEach((parentId) => {
      const subVenues = subVenuesByParent.get(parentId) || [];
      if (subVenues.length > 0) {
        subVenues.forEach((subVenue) => map.set(helpers.getObjId(subVenue), subVenue));
        if (parentHasGames.has(parentId)) {
          const parent = byId.get(parentId);
          if (parent) map.set(parentId, parent);
        }
      } else {
        const parent = byId.get(parentId);
        if (parent) map.set(parentId, parent);
      }
    });

    const gamesOnDay = games.filter(
      (game) => toDateKey(game.globalDate || game.date) === selectedDateKey,
    );
    const withUnassigned = (lanes) =>
      appendUnassignedLane(lanes, gamesOnDay, (g) => getLocationIdFromGame(g, locationMatchList));

    const ordered = Array.from(map.values());
    ordered.sort((a, b) => {
      const aParent = helpers.getObjId(a.parentLocation);
      const bParent = helpers.getObjId(b.parentLocation);
      const aParentName = aParent
        ? (locationMatchList.find((loc) => helpers.getObjId(loc) === aParent)?.name || '')
        : (a.name || '');
      const bParentName = bParent
        ? (locationMatchList.find((loc) => helpers.getObjId(loc) === bParent)?.name || '')
        : (b.name || '');
      const parentCompare = aParentName.localeCompare(bParentName);
      if (parentCompare !== 0) return parentCompare;
      const aIsSub = Boolean(aParent);
      const bIsSub = Boolean(bParent);
      if (aIsSub !== bIsSub) return aIsSub ? 1 : -1;
      return (a.name || '').localeCompare(b.name || '');
    });

    // No time slot / game on the selected date (e.g. a season with no schedule
    // yet): fall back to all of the org's selectable locations — sub-venues
    // (courts) plus standalone venues that have no sub-venues — so the Create
    // Game dropdown + enabled-state reflect "does this org have locations".
    if (ordered.length === 0) {
      const hasSub = new Set<string>();
      locationMatchList.forEach((l) => {
        const p = helpers.getObjId(l.parentLocation);
        if (p) hasSub.add(p);
      });
      return withUnassigned(locationMatchList.filter((l) =>
        helpers.getObjId(l.parentLocation) || !hasSub.has(helpers.getObjId(l))
      ));
    }

    return withUnassigned(ordered);
  }, [games, selectedDateKey, timeSlots, locationMatchList]);

  // ─── Calendar data ──────────────────────────────────────────────────────────
  const gamesForDay = useMemo(
    () => games.filter((game) => toDateKey(game.globalDate || game.date) === selectedDateKey),
    [games, selectedDateKey],
  );

  const bounds = useMemo(() => {
    // Collect the day's earliest game start + earliest/latest slot bounds. The
    // grid END still grows to encompass everything (games + slots) so there's
    // room to drag cards below; the grid START, however, is anchored to the
    // first GAME (not slots) so it hugs the games instead of wasting space.
    const gameStarts: number[] = [];
    let max = 19 * 60;
    let earliestSlot = Infinity;
    timeSlots.forEach((slot) => {
      const slotDateKey = toDateKey(slot.availableDate);
      if (slotDateKey !== selectedDateKey) return;
      earliestSlot = Math.min(earliestSlot, parseTimeToMinutes(slot.startTime));
      max = Math.max(max, parseTimeToMinutes(slot.endTime));
    });
    gamesForDay.forEach((game) => {
      const start = parseTimeToMinutes(game.time);
      const duration = game.duration || defaultDuration;
      gameStarts.push(start);
      max = Math.max(max, start + duration);
    });

    // Auto start = 1 hour before the first game; fall back to earliest slot,
    // then 9 AM when the day has neither.
    const anchor = gameStarts.length
      ? Math.min(...gameStarts) - 60
      : Number.isFinite(earliestSlot)
        ? earliestSlot
        : 9 * 60;
    // A manual override may only move the start EARLIER (never clip a game).
    const effectiveStart = gridStartOverride != null ? Math.min(gridStartOverride, anchor) : anchor;

    let min = clamp(Math.floor(effectiveStart / SNAP_MINUTES) * SNAP_MINUTES, 0, 24 * 60 - SNAP_MINUTES);
    max = clamp(Math.ceil((max + 30) / SNAP_MINUTES) * SNAP_MINUTES, SNAP_MINUTES, 24 * 60);
    if (max - min < 8 * 60) max = clamp(min + 8 * 60, SNAP_MINUTES, 24 * 60);
    return { start: min, end: max };
  }, [gamesForDay, selectedDateKey, timeSlots, gridStartOverride]);

  const totalHeight = ((bounds.end - bounds.start) / SNAP_MINUTES) * SLOT_HEIGHT;

  const gamesByLocation = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    locations.forEach((location) => { grouped[location._id] = []; });
    gamesForDay.forEach((game) => {
      const draft = draftByGameId[game._id];
      const locationId = draft?.locationId
        || getLocationIdFromGame(game, locationMatchList)
        // Location-less game: land in the synthetic unassigned lane when it's
        // rendered (the `locations` memo appends it whenever such games exist).
        || (grouped[UNASSIGNED_LANE_ID] ? UNASSIGNED_LANE_ID : null);
      if (!locationId) return;
      if (!grouped[locationId]) grouped[locationId] = [];
      grouped[locationId].push(game);
    });
    Object.keys(grouped).forEach((locationId) => {
      grouped[locationId].sort((a, b) => parseTimeToMinutes(a.time) - parseTimeToMinutes(b.time));
    });
    return grouped;
  }, [gamesForDay, locations, locationMatchList, draftByGameId]);

  const timeSlotRangesByLocation = useMemo(() => {
    const ranges: Record<string, { start: number; end: number }[]> = {};
    timeSlots.forEach((slot) => {
      const slotDateKey = toDateKey(slot.availableDate);
      if (slotDateKey !== selectedDateKey) return;
      const locationId = helpers.getObjId(slot.location);
      if (!locationId) return;
      if (!ranges[locationId]) ranges[locationId] = [];
      ranges[locationId].push({
        start: parseTimeToMinutes(slot.startTime),
        end: parseTimeToMinutes(slot.endTime),
      });
    });
    return ranges;
  }, [selectedDateKey, timeSlots]);

  // Measure the width available for the schedule. The schedule sits inside the
  // shared `.league-layout-main-content` column, a `flex: 1` item with the default
  // `min-width: auto` — so if we read our own container's clientWidth, the column
  // grows to fit the calendar we size from it, and the calendar grows to fit the
  // column: an unbounded ResizeObserver feedback loop. Instead we derive the
  // available width purely from content-INDEPENDENT values — the viewport width and
  // the column's fixed left margin / paddings — so the result can't feed back.
  // Available = viewport − column margin/padding − every wrapper's L+R inset down to
  // the calendar. All terms are stable, so this converges immediately.
  useLayoutEffect(() => {
    const el = scheduleMeasureRef.current;
    if (!el) return undefined;
    const update = () => {
      const column = (el.closest('.league-layout-main-content') as HTMLElement | null) || el.parentElement;
      if (!column) return;
      const colCs = getComputedStyle(column);
      let avail = document.documentElement.clientWidth
        - (parseFloat(colCs.marginLeft) || 0) - (parseFloat(colCs.marginRight) || 0)
        - (parseFloat(colCs.paddingLeft) || 0) - (parseFloat(colCs.paddingRight) || 0);
      // Subtract padding/border/margin of every wrapper between the column and us.
      for (let node: HTMLElement | null = el; node && node !== column; node = node.parentElement) {
        const cs = getComputedStyle(node);
        avail -= (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0)
          + (parseFloat(cs.borderLeftWidth) || 0) + (parseFloat(cs.borderRightWidth) || 0)
          + (parseFloat(cs.marginLeft) || 0) + (parseFloat(cs.marginRight) || 0);
      }
      // Safety margin absorbs the calendar's own border (1px each side) + subpixel
      // rounding so filling the column never trips a 1–2px page scrollbar.
      avail = Math.max(0, avail - 4);
      // Guard so a sub-pixel wobble can't retrigger the resize cycle.
      setContainerWidth((prev) => (Math.abs(prev - avail) > 1 ? avail : prev));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [locations.length]);

  // Court-column width: fill the available container width, min COLUMN_WIDTH.
  const columnWidth = useMemo(() => {
    if (!containerWidth || locations.length === 0) return COLUMN_WIDTH;
    const avail = containerWidth - TIME_GUTTER_WIDTH;
    return Math.max(COLUMN_WIDTH, Math.floor(avail / locations.length));
  }, [containerWidth, locations.length]);

  // How many overlapping game cards fit side-by-side in one (wider) column.
  const maxVisibleColumns = useMemo(() => {
    const inner = columnWidth - GAME_INSET * 2;
    return Math.max(
      MAX_VISIBLE_COLUMNS,
      Math.floor((inner + GAME_GAP) / (MIN_CARD_WIDTH + GAME_GAP)),
    );
  }, [columnWidth]);

  const layoutByLocation = useMemo(() => {
    const layout: Record<string, { items: any[]; groups: any[] }> = {};
    locations.forEach((location) => {
      const locationGames = gamesByLocation[location._id] || [];
      layout[location._id] = buildOverlapLayout(locationGames, draftByGameId, maxVisibleColumns, defaultDuration);
    });
    return layout;
  }, [locations, gamesByLocation, draftByGameId, maxVisibleColumns, defaultDuration]);

  const quarterTicks = useMemo(() => {
    const ticks = [];
    for (let minute = bounds.start; minute <= bounds.end; minute += SNAP_MINUTES) {
      ticks.push(minute);
    }
    return ticks;
  }, [bounds.start, bounds.end]);

  // ─── Drag & resize ──────────────────────────────────────────────────────────
  useEffect(() => {
    draftByGameIdRef.current = draftByGameId;
  }, [draftByGameId]);

  const commitDraftUpdate = useCallback(
    async (gameId: string, start: number, duration: number, locationIdOverride?: string) => {
      const game = games.find((g) => g._id === gameId);
      if (!game) return;

      const laneId = locationIdOverride || getLocationIdFromGame(game, locationMatchList);
      // Synthetic lane ids (unassigned / name:<string>) must never be written
      // into the `locationModel` ObjectId ref — dropping into those lanes only
      // moves the game in time and leaves its location fields untouched.
      const locationId = isRealLocationId(laneId) ? laneId : null;
      const matchedTimeSlot = locationId
        ? getTimeSlotForGame(timeSlots, selectedDateKey, locationId, start)
        : null;

      const updatedGame = {
        ...game,
        time: formatMinutesForGameTime(start),
        duration,
        globalDate: toGlobalDate(selectedDateKey),
        date: toGlobalDate(selectedDateKey),
        timeSlot: matchedTimeSlot?._id,
        locationModel: locationId || helpers.getObjId(game.locationModel),
        location: locationMatchList.find((l) => l._id === locationId)?.name || game.location,
      };
      if (updatedGame.playoffRound == null) updatedGame.playoffRound = '';

      await updateGame(updatedGame);
      await getGamesByDivisionId(divisionId);
    },
    [games, locationMatchList, selectedDateKey, timeSlots, updateGame, getGamesByDivisionId, divisionId],
  );

  useEffect(() => {
    if (!interaction) return;

    const onMove = (event: MouseEvent) => {
      const deltaY = event.clientY - interaction.startClientY;
      const deltaSlots = Math.round(deltaY / SLOT_HEIGHT);
      const deltaMinutes = deltaSlots * SNAP_MINUTES;

      if (interaction.mode === 'move') {
        const nextStart = clamp(
          interaction.initialStart + deltaMinutes,
          bounds.start,
          bounds.end - interaction.initialDuration,
        );
        let nextLocationId = interaction.initialLocationId;
        if (calendarRef.current && locations.length > 0) {
          const rect = calendarRef.current.getBoundingClientRect();
          const localX = event.clientX - rect.left + calendarRef.current.scrollLeft;
          const columnIndex = Math.floor((localX - TIME_GUTTER_WIDTH) / columnWidth);
          if (columnIndex >= 0 && columnIndex < locations.length) {
            nextLocationId = locations[columnIndex]._id;
          }
        }
        setDraftByGameId((prev) => ({
          ...prev,
          [interaction.gameId]: { start: nextStart, duration: interaction.initialDuration, locationId: nextLocationId },
        }));
      }

      if (interaction.mode === 'resize') {
        const nextDuration = clamp(
          interaction.initialDuration + deltaMinutes,
          SNAP_MINUTES,
          bounds.end - interaction.initialStart,
        );
        setDraftByGameId((prev) => ({
          ...prev,
          [interaction.gameId]: { start: interaction.initialStart, duration: nextDuration },
        }));
      }
    };

    const onUp = async () => {
      const draft = draftByGameIdRef.current[interaction.gameId];
      setInteraction(null);
      document.body.style.userSelect = '';

      if (!draft) return;

      const didChange =
        draft.start !== interaction.initialStart ||
        draft.duration !== interaction.initialDuration ||
        draft.locationId !== interaction.initialLocationId;

      if (!didChange) {
        setDraftByGameId((prev) => {
          const next = { ...prev };
          delete next[interaction.gameId];
          return next;
        });
        return;
      }

      try {
        await commitDraftUpdate(interaction.gameId, draft.start, draft.duration, draft.locationId);
        toast.success('Game updated');
      } catch (error) {
        toast.error('Failed to update game');
      } finally {
        setDraftByGameId((prev) => {
          const next = { ...prev };
          delete next[interaction.gameId];
          return next;
        });
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
  }, [interaction, bounds.start, bounds.end, commitDraftUpdate, locations, columnWidth]);

  const onStartMove = (event: React.MouseEvent, game: any) => {
    event.preventDefault();
    event.stopPropagation();
    const gameId = game._id;
    const currentStart = draftByGameId[gameId]?.start ?? parseTimeToMinutes(game.time);
    const currentDuration = draftByGameId[gameId]?.duration ?? (game.duration || defaultDuration);
    const currentLocationId = draftByGameId[gameId]?.locationId || getLocationIdFromGame(game, locationMatchList);
    document.body.style.userSelect = 'none';
    setInteraction({
      mode: 'move',
      gameId,
      startClientY: event.clientY,
      initialStart: currentStart,
      initialDuration: currentDuration,
      initialLocationId: currentLocationId,
    });
  };

  const onStartResize = (event: React.MouseEvent, game: any) => {
    event.preventDefault();
    event.stopPropagation();
    const gameId = game._id;
    const currentStart = draftByGameId[gameId]?.start ?? parseTimeToMinutes(game.time);
    const currentDuration = draftByGameId[gameId]?.duration ?? (game.duration || defaultDuration);
    document.body.style.userSelect = 'none';
    setInteraction({
      mode: 'resize',
      gameId,
      startClientY: event.clientY,
      initialStart: currentStart,
      initialDuration: currentDuration,
    });
  };

  // ─── Create game ─────────────────────────────────────────────────────────────
  const openCreateModal = () => {
    setCreateFormPoolTeams([]);
    setCreateForm({
      homeTeamId: '',
      awayTeamId: '',
      locationId: selectableLocations[0]?._id || '',
      dateKey: selectedDateKey || toDateKey(new Date()),
      startMinutes: 9 * 60,
      duration: defaultDuration,
      homeScore: '',
      awayScore: '',
    });
    setIsCreateOpen(true);
  };

  // INT-0027: the scheduler first-run panels deep-link here with ?create=1
  // to open the quick-create modal directly (one click from "no games yet"
  // to the form). Fires once; the param is then cleared from the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenedRef = React.useRef(false);
  React.useEffect(() => {
    if (autoOpenedRef.current) return;
    if (searchParams.get('create') !== '1') return;
    autoOpenedRef.current = true;
    openCreateModal();
    searchParams.delete('create');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openCreateModalForPool = useCallback((poolTeams: any[]) => {
    setCreateFormPoolTeams(poolTeams);
    setCreateForm({
      homeTeamId: '',
      awayTeamId: '',
      locationId: selectableLocations[0]?._id || '',
      dateKey: selectedDateKey || toDateKey(new Date()),
      startMinutes: 9 * 60,
      duration: defaultDuration,
      homeScore: '',
      awayScore: '',
    });
    setIsCreateOpen(true);
  }, [locations, selectedDateKey]);

  const handleReassignTeam = useCallback(async (teamId: string, toPoolId: number) => {
    if (!division?.config?.pools) return;
    const token = await getToken();
    const pools = division.config.pools.map((pool) => ({
      ...pool,
      teamIds: pool.id === toPoolId
        ? [...pool.teamIds.filter((id) => id !== teamId), teamId]
        : pool.teamIds.filter((id) => id !== teamId),
    }));
    const updated = { ...division, config: { ...division.config, pools } };
    await new Promise<void>((resolve) => updateDivision(updated, token, () => resolve()));
  }, [division, getToken, updateDivision]);

  const openEditPoolAssignments = useCallback(() => {
    if (!division?.config?.pools) return;
    const assignments: Record<string, number> = {};
    for (const pool of division.config.pools) {
      for (const teamId of pool.teamIds) {
        assignments[teamId] = pool.id;
      }
    }
    setEditPoolAssignments(assignments);
    setEditPoolsOpen(true);
  }, [division]);

  const handleSavePoolAssignments = async () => {
    if (!division?.config?.pools) return;
    setIsSavingPoolAssignments(true);
    try {
      const token = await getToken();
      const pools = division.config.pools.map((pool) => ({
        ...pool,
        teamIds: divisionTeams
          .filter((t) => editPoolAssignments[t._id] === pool.id)
          .map((t) => t._id),
      }));
      const updated = { ...division, config: { ...division.config, pools } };
      await new Promise<void>((resolve) => updateDivision(updated, token, () => resolve()));
      setEditPoolsOpen(false);
    } finally {
      setIsSavingPoolAssignments(false);
    }
  };

  const onCreateGame = async () => {
    // INT-0027: location is OPTIONAL — a game is date+time+teams; venue can
    // come later. Server accepts location-less games (schema: location/
    // locationModel/timeSlot all optional).
    if (!createForm.homeTeamId || !createForm.awayTeamId) {
      toast.error('Please pick both teams');
      return;
    }
    if (createForm.homeTeamId === createForm.awayTeamId) {
      toast.error('Home and away team must be different');
      return;
    }
    if (!division) {
      toast.error('Division not found');
      return;
    }

    const location = createForm.locationId
      ? selectableLocations.find((loc) => loc._id === createForm.locationId)
      : null;
    if (createForm.locationId && !location) {
      toast.error('Invalid location');
      return;
    }

    const isObjectId = (v: any) => typeof v === 'string' && /^[a-f\d]{24}$/i.test(v);
    const dateKey = createForm.dateKey || selectedDateKey;
    if (!dateKey) {
      toast.error('Please select a date');
      return;
    }
    const slot = createForm.locationId
      ? getTimeSlotForGame(timeSlots, dateKey, createForm.locationId, createForm.startMinutes)
      : undefined;

    const payload = {
      division: divisionId,
      organization: helpers.getObjId(division.organization),
      season: helpers.getObjId(division.season) || helpers.getObjId(division.league),
      league: helpers.getObjId(division.league),
      homeTeam: createForm.homeTeamId,
      awayTeam: createForm.awayTeamId,
      // `locationModel` is an ObjectId ref to Location; only send a real Location
      // _id. Synthetic dropdown entries for string-only locations carry a name
      // string as their _id — keep the display name in `location` and omit the ref.
      ...(location && isObjectId(createForm.locationId) ? { locationModel: createForm.locationId } : {}),
      ...(location ? { location: getLocationDisplayName(location) } : {}),
      globalDate: toGlobalDate(dateKey),
      date: toGlobalDate(dateKey),
      time: formatMinutesForGameTime(createForm.startMinutes),
      timeSlot: slot?._id,
      duration: createForm.duration,
      homeScore: createForm.homeScore === '' ? null : Number(createForm.homeScore),
      awayScore: createForm.awayScore === '' ? null : Number(createForm.awayScore),
      sport: Array.isArray(division.sports) && division.sports.length > 0 ? division.sports[0] : 'basketball',
    };

    try {
      await addGame(payload);
      await getGamesByDivisionId(divisionId);
      toast.success('Game created');
      setIsCreateOpen(false);
    } catch (error) {
      toast.error('Failed to create game');
    }
  };

  // ─── Add team ────────────────────────────────────────────────────────────────
  const openAddTeamDialog = async () => {
    setAddTeamForm({ teamName: '', players: [], sendInvite: false, inviteEmail: '', inviteFirstName: '', inviteLastName: '', forcePlace: false, ignoreDeadline: false });
    setRegistration(null);
    setIsAddTeamOpen(true);
    if (!leagueId) return;
    setIsRegistrationLoading(true);
    if (tournamentId) {
      await Promise.all([
        getTournamentById(tournamentId),
        getRegistrationByTournamentId(tournamentId, (reg: any) => {
          setRegistration(reg);
        }),
      ]);
    } else {
      await getRegistrationByLeagueId(leagueId, (reg: any) => {
        setRegistration(reg);
      });
    }
    setIsRegistrationLoading(false);
  };

  // Effective config respects the precedence: division > season > tournament
  // (the registration is NOT a tier — ADR 0034).
  const effectiveConfig = useMemo(() => getConfig({
    division: _.get(division, 'config', {}),
    season: _.get(league, 'config', {}),
    tournament: _.get(tournament, 'config', {}),
  }), [division, league, tournament]);

  // `excludeTeamName` (resolved via the config hierarchy — commonly set at the
  // tournament level for doubles/pairs formats): the team is identified by its
  // players, so the team name is excluded (auto-generated) and a roster is
  // required. Admin Add Team is always a quick team-only add (no RegistrationTeam);
  // `collectRoster` (== excludeTeamName) is the only case that collects players.
  // Individual/doubles sports default to no team name when nothing in the
  // hierarchy sets it explicitly (ADR 0049) — sport mirror per ADR 0010.
  const excludeTeamName = typeof effectiveConfig.excludeTeamName === 'boolean'
    ? effectiveConfig.excludeTeamName
    : isIndividualSport(_.get(division, 'sports[0]') || _.get(league, 'sports[0]'));
  const collectRoster = excludeTeamName;

  const addTeamValidation = useMemo(() => {
    const errors: string[] = [];
    const isMissing = (v: any) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

    // Team name is required UNLESS excludeTeamName is on (then it's auto-synthesized).
    const teamNameMissing = !excludeTeamName && isMissing(addTeamForm.teamName);
    if (teamNameMissing) errors.push('Team name is required');

    let missingPlayerFields: Set<string>[] = [];

    // Roster: required for excludeTeamName teams (identified by their players). For
    // excludeTeamName with no explicit minPlayers, a nameless team still needs ≥1.
    if (collectRoster) {
      const minPlayers = effectiveConfig.minPlayers > 0
        ? effectiveConfig.minPlayers
        : (excludeTeamName ? 1 : 0);
      if (minPlayers > 0 && addTeamForm.players.length < minPlayers) {
        errors.push(`At least ${minPlayers} player${minPlayers > 1 ? 's' : ''} required`);
      }
      if (effectiveConfig.maxPlayers > 0 && addTeamForm.players.length > effectiveConfig.maxPlayers) {
        errors.push(`No more than ${effectiveConfig.maxPlayers} players allowed`);
      }

      const activePlayerFields = (registration?.fields || []).filter((f: any) => f.include !== false);
      missingPlayerFields = addTeamForm.players.map((player: any) => {
        const set = new Set<string>();
        if (activePlayerFields.length === 0) {
          // No custom fields → a player needs at least a name (single field or first/last).
          if (isMissing(player?.info?.name) && isMissing(player?.info?.first_name)) set.add('name');
        } else {
          for (const f of activePlayerFields) {
            if (!f.required) continue;
            if (isMissing(player?.info?.[f.fieldId])) set.add(f.fieldId);
          }
        }
        return set;
      });
      if (missingPlayerFields.some((s) => s.size > 0)) errors.push('Some player fields are missing');
    }

    // Manager invite: when enabled, a valid email is required.
    const inviteEmailMissing = addTeamForm.sendInvite && isMissing(addTeamForm.inviteEmail);
    const inviteEmailInvalid = addTeamForm.sendInvite
      && !isMissing(addTeamForm.inviteEmail)
      && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addTeamForm.inviteEmail.trim());
    if (inviteEmailMissing) errors.push('Manager email is required to send an invite');
    if (inviteEmailInvalid) errors.push('Manager email is invalid');

    // Deadline: inviting after the registration end date needs the explicit
    // "Ignore registration deadline" acknowledgment.
    const registrationClosed = !!registration?._id && isAfterRegistrationEnd(registration);
    if (addTeamForm.sendInvite && registrationClosed && !addTeamForm.ignoreDeadline) {
      errors.push('Registration is closed. Check "Ignore registration deadline" to invite anyway');
    }

    return {
      valid: errors.length === 0,
      errors,
      teamNameMissing,
      missingPlayerFields,
      inviteEmailMissing,
      inviteEmailInvalid,
      registrationClosed,
    };
  }, [addTeamForm, effectiveConfig, registration, excludeTeamName, collectRoster]);

  const onAddTeam = async () => {
    if (!division) {
      toast.error('Division not loaded');
      return;
    }
    if (!excludeTeamName && !addTeamForm.teamName.trim()) {
      toast.error('Team name is required');
      return;
    }

    // excludeTeamName teams have no name field — always auto-generate a zqtm_ name
    // in the background (getTeamDisplayName renders it as the players' names).
    const teamName = excludeTeamName ? generateAutoTeamName() : addTeamForm.teamName.trim();
    const leagueRef = helpers.getObjId(division.league);
    const organizationRef = helpers.getObjId(division.organization);

    try {
      // When the division has a registration, the backend also creates + links a
      // lightweight RegistrationTeam (badge "Registered") and holds the team OFF
      // the public page (active:false) until accepted — unless forcePlace, which
      // marks the RT accepted (+disableSync) and activates the team immediately
      // (payment/waiver handled on paper). No registration → team-only add, as before.
      const registrationTeamId: string | null = null;

      const dtPayload: any = {
        name: teamName,
        team: null,
        division: divisionId,
        league: leagueRef,
        organization: organizationRef,
        seed: -1,
        active: true,
      };
      if (registration?._id) {
        dtPayload.registration = helpers.getObjId(registration);
        dtPayload.createRegistrationTeam = true;
        dtPayload.forcePlace = !!addTeamForm.forcePlace;
        dtPayload.allowLateRegistration = !!addTeamForm.ignoreDeadline;
      }

      const createdDT = await addTeamDivision(dtPayload);
      const divisionTeamId = createdDT?._id;

      let createdPlayers = 0;
      let failedPlayers = 0;
      // Create real Player docs whenever we collected a roster (registration-fields
      // mode OR an excludeTeamName team). Uses the shared buildPlayerPayload so the
      // create body matches the team page's add-player flow.
      if (collectRoster && divisionTeamId && addTeamForm.players.length > 0) {
        const token = await getToken();
        const authHeader = { headers: { Authorization: `Bearer ${token}` } };
        for (let idx = 0; idx < addTeamForm.players.length; idx++) {
          const p = addTeamForm.players[idx];
          try {
            const refs: any = { team: divisionTeamId, league: leagueRef, organization: organizationRef };
            if (registration?._id) refs.registration = helpers.getObjId(registration);
            if (registrationTeamId) refs.registrationTeam = registrationTeamId;
            if (tournamentId) refs.tournament = tournamentId;
            await authedAxios.post(`${process.env.API_URL}/api/v1/players/d`, buildPlayerPayload(p.info, refs, idx), authHeader);
            createdPlayers += 1;
          } catch (err) {
            console.error('Failed to create player', idx, err);
            failedPlayers += 1;
          }
        }
      }

      await getTeamDivisionsByDivisionId(divisionId);
      if (divisionFormat === 'pool-play' || divisionFormat === 'pool-play-playoffs') {
        const freshTeams = useTeamDivisionStore.getState().teamDivisions.filter(
          (t) => helpers.getObjId(t.division) === divisionId
        );
        console.log('[Pool] onAddTeam: divisionFormat=', divisionFormat, 'freshTeams=', freshTeams.length);
        await reconfigurePools(freshTeams);
      }

      if (collectRoster && addTeamForm.players.length > 0) {
        const summary = failedPlayers > 0
          ? `Team added · ${createdPlayers} player(s) created, ${failedPlayers} failed`
          : `Team added · ${createdPlayers} player(s) created`;
        failedPlayers > 0 ? toast.warning(summary) : toast.success(summary);
      } else {
        toast.success('Team added');
      }

      // Optional: email the team manager a registration invite. The backend
      // creates a linked (pending) RegistrationTeam so the invite link preselects
      // this division and the manager completes THIS team (no duplicate).
      if (addTeamForm.sendInvite && addTeamForm.inviteEmail.trim() && divisionTeamId) {
        await inviteManager(divisionTeamId, {
          email: addTeamForm.inviteEmail.trim(),
          firstName: addTeamForm.inviteFirstName.trim(),
          lastName: addTeamForm.inviteLastName.trim(),
          allowLateRegistration: !!addTeamForm.ignoreDeadline,
        });
        await getTeamDivisionsByDivisionId(divisionId);
      }

      setIsAddTeamOpen(false);
    } catch (error) {
      console.error('onAddTeam failed:', error);
      toast.error('Failed to add team');
    }
  };

  // ─── Round Robin ─────────────────────────────────────────────────────────────
  const divisionGames = useMemo(
    () => games.filter((g) => helpers.getObjId(g.division) === divisionId),
    [games, divisionId],
  );

  const refreshGames = useCallback(() => getGamesByDivisionId(divisionId), [getGamesByDivisionId, divisionId]);

  // Games still needing results — same predicate as the API's unscoredByOrganization.
  // NB: gameStore remaps isCancelled to the string 'YES'/'NO' (see getGamesByDivisionId).
  const unscoredDivisionGames = useMemo(
    () => divisionGames.filter(
      (g) => g.isCancelled !== 'YES' && g.isCancelled !== true && (g.homeScore == null || g.awayScore == null),
    ),
    [divisionGames],
  );

  const numTeams = divisionTeams.length;
  const isEvenTeams = numTeams % 2 === 0;
  const roundsPerMatchup = _.get(division, 'config.roundsPerMatchup', 1) || 1;
  const numRounds = numTeams < 2 ? 0 : (isEvenTeams ? numTeams - 1 : numTeams) * roundsPerMatchup;
  const gamesPerRound = numTeams < 2 ? 0 : isEvenTeams ? numTeams / 2 : Math.floor(numTeams / 2);

  // ─── Auto-populate games from the division format ────────────────────────────
  // Reuses the same matchups the scoresheet computes and creates real Game
  // records. All games share the selected date + a single 9:00 AM start time,
  // spread across the available courts. Only enabled once a location exists.
  const AUTO_POPULATE_START_MINUTES = 9 * 60;

  const autoPopulateMatchups = useMemo(
    () => resolveMatchups(divisionFormat, { division, teams: divisionTeams }),
    [divisionFormat, division, divisionTeams],
  );

  // Standalone bracket formats generate one game per slot instead of rounds.
  const autoPopulateBracketGames = useMemo(
    () => resolveBracketGames(divisionFormat, { division, teams: divisionTeams }),
    [divisionFormat, division, divisionTeams],
  );

  // Hybrid formats (group stage + playoffs) generate the playoff bracket
  // separately from the group-stage games, all sides as placeholders —
  // round 1 is filled from standings once the group stage is decided.
  const isHybridPlayoffFormat =
    divisionFormat === 'round-robin-playoffs' || divisionFormat === 'pool-play-playoffs';
  const hybridPlayoffSize = normalizeBracketSize(
    _.get(division, 'config.playoffTeams', defaultPlayoffTeams(divisionFormat)),
    false,
  );
  // Pure bracket formats (bracket / playoffs / playoffs-consolation). The
  // Playoff Bracket Size picker lives as a subsection INSIDE the Division
  // Format card (owner 2026-10-09), shown only for these formats; the value
  // also drives the BracketScheduler below.
  const isBracketFormat =
    divisionFormat === 'bracket' || divisionFormat === 'playoffs' || divisionFormat === 'playoffs-consolation';
  const isBracketConsolation = isConsolationFormat(divisionFormat);
  const bracketSize = normalizeBracketSize(
    _.get(division, 'config.playoffTeams', defaultPlayoffTeams(divisionFormat)),
    isBracketConsolation,
  );
  const hybridPlayoffBracketGames = useMemo(
    () => (isHybridPlayoffFormat ? resolvePlayoffBracketGames(hybridPlayoffSize) : []),
    [isHybridPlayoffFormat, hybridPlayoffSize],
  );

  const existingGeneratedGames = useMemo(
    () => divisionGames.filter((g) => g.competitionMeta != null),
    [divisionGames],
  );
  // Group-stage vs playoff-bracket generated games have independent
  // regeneration lifecycles for hybrid formats — replacing one must not
  // delete the other (the group stage is usually played before the bracket).
  const generatedBracketGames = useMemo(
    () => existingGeneratedGames.filter((g) => playoffKeyOf(g.competitionMeta) != null),
    [existingGeneratedGames],
  );
  const generatedGroupGames = useMemo(
    () => existingGeneratedGames.filter((g) => playoffKeyOf(g.competitionMeta) == null),
    [existingGeneratedGames],
  );
  // What Auto-Populate deletes before re-creating: for standalone bracket
  // formats it IS the bracket generator, so it replaces everything; for
  // group-stage formats it leaves the playoff bracket alone.
  const autoPopulateReplaceTargets = canAutoPopulateBracket(divisionFormat)
    ? existingGeneratedGames
    : generatedGroupGames;

  // One Game payload per resolved bracket slot — shared by the standalone
  // bracket Auto-Populate and the hybrid "Generate Playoff Bracket" flow.
  const buildBracketPayloads = (bracketGames: any[]) => {
    const isObjectId = (v: any) => typeof v === 'string' && /^[a-f\d]{24}$/i.test(v);
    const dateKey = selectedDateKey || toDateKey(new Date());
    const globalDate = toGlobalDate(dateKey);
    const time = formatMinutesForGameTime(AUTO_POPULATE_START_MINUTES);
    const duration = division?.config?.gameDurationInMinutes || defaultDuration;
    const sport = Array.isArray(division?.sports) && division.sports.length > 0 ? division.sports[0] : 'basketball';

    return bracketGames.map((b, idx) => {
      const loc = selectableLocations[idx % selectableLocations.length];
      const locId = loc?._id;
      const slot = isObjectId(locId)
        ? getTimeSlotForGame(timeSlots, dateKey, locId, AUTO_POPULATE_START_MINUTES)
        : undefined;
      return {
        division: divisionId,
        organization: helpers.getObjId(division.organization),
        season: helpers.getObjId(division.season) || helpers.getObjId(division.league),
        league: helpers.getObjId(division.league),
        // Seed-fed games take known teams; everything else starts empty with a
        // placeholder ("Seed 3" / "Winner of Semifinal 1") and is filled by the
        // API's bracket advancement (or the admin) as results come in.
        homeTeam: b.homeTeamId ?? null,
        awayTeam: b.awayTeamId ?? null,
        homeTeamPlaceholder: b.homeTeamPlaceholder || '',
        awayTeamPlaceholder: b.awayTeamPlaceholder || '',
        ...(isObjectId(locId) ? { locationModel: locId } : {}),
        location: loc ? getLocationDisplayName(loc) : '',
        globalDate,
        date: globalDate,
        time,
        timeSlot: slot?._id,
        duration,
        homeScore: null,
        awayScore: null,
        sport,
        gameLabel: b.gameLabel,
        playoffRound: b.playoffRound,
        competitionMeta: { stage: b.stage, bracketSlot: b.bracketSlot },
      };
    });
  };

  const buildAutoPopulatePayloads = () => {
    const isObjectId = (v: any) => typeof v === 'string' && /^[a-f\d]{24}$/i.test(v);
    const dateKey = selectedDateKey || toDateKey(new Date());
    const globalDate = toGlobalDate(dateKey);
    const time = formatMinutesForGameTime(AUTO_POPULATE_START_MINUTES);
    const duration = division?.config?.gameDurationInMinutes || defaultDuration;
    const sport = Array.isArray(division?.sports) && division.sports.length > 0 ? division.sports[0] : 'basketball';

    if (canAutoPopulateBracket(divisionFormat)) {
      return buildBracketPayloads(autoPopulateBracketGames);
    }

    // Game numbers are per-round (per pool+round for pool play) — that's how the
    // schedulers match a game to its slot (getSlotGame keys on round[+pool]+gameNumber).
    // A global index would only fill Round 1; every later round's slots expect 1..N again.
    const gameNumberByKey: Record<string, number> = {};
    return autoPopulateMatchups.map((m, idx) => {
      const loc = selectableLocations[idx % selectableLocations.length];
      const locId = loc?._id;
      const slot = isObjectId(locId)
        ? getTimeSlotForGame(timeSlots, dateKey, locId, AUTO_POPULATE_START_MINUTES)
        : undefined;
      const roundKey = `${m.pool ?? ''}-${m.round}`;
      gameNumberByKey[roundKey] = (gameNumberByKey[roundKey] || 0) + 1;
      return {
        division: divisionId,
        organization: helpers.getObjId(division.organization),
        season: helpers.getObjId(division.season) || helpers.getObjId(division.league),
        league: helpers.getObjId(division.league),
        homeTeam: m.homeTeamId,
        awayTeam: m.awayTeamId,
        // `locationModel` is an ObjectId ref; only send a real Location _id (see onCreateGame).
        ...(isObjectId(locId) ? { locationModel: locId } : {}),
        location: loc ? getLocationDisplayName(loc) : '',
        globalDate,
        date: globalDate,
        time,
        timeSlot: slot?._id,
        duration,
        homeScore: null,
        awayScore: null,
        sport,
        // Stamp round/pool so games tie back to "each Round" and the existing
        // clear-generated-games logic recognizes them.
        competitionMeta: {
          round: m.round,
          ...(m.pool != null ? { pool: m.pool } : {}),
          gameNumber: gameNumberByKey[roundKey],
        },
      };
    });
  };

  const runAutoPopulate = async () => {
    setIsAutoPopulating(true);
    try {
      // Replace previously generated games so re-running doesn't duplicate
      // them. Hybrid formats only replace their group-stage games here — the
      // playoff bracket has its own generate/replace flow.
      if (autoPopulateReplaceTargets.length > 0) {
        await Promise.all(autoPopulateReplaceTargets.map((g) => deleteGame(g._id)));
      }
      const payloads = buildAutoPopulatePayloads();
      await addBulkGames(payloads);
      await refreshGames();
      toast.success(`Created ${payloads.length} game${payloads.length === 1 ? '' : 's'}`);
      setAutoPopulateOpen(false);
    } catch (error) {
      toast.error('Failed to auto-populate games');
    } finally {
      setIsAutoPopulating(false);
    }
  };

  const handleAutoPopulateClick = () => {
    if (!division) {
      toast.error('Division not found');
      return;
    }
    // No location required — with zero courts the payload builders leave
    // location/locationModel/timeSlot unset and admins assign courts later.
    // Bracket formats always resolve a full slot list (missing teams become
    // "Seed N" placeholders), so only the group-stage formats can be empty.
    if (!canAutoPopulateBracket(divisionFormat) && autoPopulateMatchups.length === 0) {
      toast.error('No matchups to generate for this format');
      return;
    }
    if (autoPopulateReplaceTargets.length > 0) {
      setAutoPopulateOpen(true); // confirm before replacing
      return;
    }
    runAutoPopulate();
  };

  // ─── Hybrid playoff bracket generation (round-robin/pool-play + playoffs) ────
  const runGeneratePlayoffBracket = async () => {
    setIsPlayoffPopulating(true);
    try {
      // Safety net: persist the bracket size the slots were generated from so
      // the API's advancement engine resolves the same geometry.
      if (division?.config?.playoffTeams == null) {
        const token = await getToken();
        const updated = { ...division, config: { ...(division.config || {}), playoffTeams: hybridPlayoffSize } };
        await new Promise<void>((resolve) => updateDivision(updated, token, () => resolve()));
      }
      // Replace only previously generated bracket games — group-stage games
      // (and their scores) are untouched.
      if (generatedBracketGames.length > 0) {
        await Promise.all(generatedBracketGames.map((g) => deleteGame(g._id)));
      }
      const payloads = buildBracketPayloads(hybridPlayoffBracketGames);
      await addBulkGames(payloads);
      await refreshGames();
      toast.success(`Created ${payloads.length} playoff game${payloads.length === 1 ? '' : 's'}`);
      setPlayoffPopulateOpen(false);
    } catch (error) {
      toast.error('Failed to generate the playoff bracket');
    } finally {
      setIsPlayoffPopulating(false);
    }
  };

  const handleGeneratePlayoffBracketClick = () => {
    if (!division) {
      toast.error('Division not found');
      return;
    }
    if (generatedBracketGames.length > 0) {
      setPlayoffPopulateOpen(true); // confirm before replacing
      return;
    }
    runGeneratePlayoffBracket();
  };

  const openEditModal = useCallback((game: any) => {
    setEditGameTarget(game);
    setEditGameForm({
      homeTeamId: helpers.getObjId(game.homeTeam) || '',
      awayTeamId: helpers.getObjId(game.awayTeam) || '',
      locationId: helpers.getObjId(game.locationModel) || '',
      dateKey: toDateKey(game.globalDate || game.date),
      startMinutes: game.time ? parseTimeToMinutes(game.time) : 9 * 60,
      duration: game.duration || defaultDuration,
      homeScore: game.homeScore ?? '',
      awayScore: game.awayScore ?? '',
    });
    setEditGameOpen(true);
  }, []);

  const onSaveEditGame = async () => {
    // Same-team guard (owner 2026-09-16) — mirror of the create modal's check.
    if (editGameForm.homeTeamId && editGameForm.homeTeamId === editGameForm.awayTeamId) {
      toast.error('Home and away team must be different');
      return;
    }
    if (!editGameTarget) return;
    const isObjectId = (v: any) => typeof v === 'string' && /^[a-f\d]{24}$/i.test(v);
    await updateGame(
      {
        ...editGameTarget,
        // null (not '') when unselected — lets a team-less game still save
        // its location/date (owner 2026-10-01); '' CastErrors the whole
        // update on the server.
        homeTeam: editGameForm.homeTeamId || null,
        awayTeam: editGameForm.awayTeamId || null,
        // Only persist a real Location _id; synthetic string-only entries keep
        // their display name in `location` (see onCreateGame).
        locationModel: isObjectId(editGameForm.locationId) ? editGameForm.locationId : null,
        // Resolve the display name from the full org location list (not the
        // date-filtered `locations` memo, which may omit the game's venue) and
        // keep the original name if not found — never overwrite with "Unknown Location".
        location: (() => {
          const loc = locationMatchList.find((l) => helpers.getObjId(l) === editGameForm.locationId);
          return loc ? getLocationDisplayName(loc) : (editGameTarget.location || '');
        })(),
        globalDate: toGlobalDate(editGameForm.dateKey),
        date: toGlobalDate(editGameForm.dateKey),
        time: formatMinutesForGameTime(editGameForm.startMinutes),
        duration: editGameForm.duration,
        homeScore: editGameForm.homeScore === '' ? null : Number(editGameForm.homeScore),
        awayScore: editGameForm.awayScore === '' ? null : Number(editGameForm.awayScore),
      },
      () => {
        toast.success('Game updated');
        setEditGameOpen(false);
      },
    );
  };

  // ─── Division format ─────────────────────────────────────────────────────────
  const handleSaveFormat = async () => {
    if (!division) return;
    setIsSavingFormat(true);
    const token = await getToken();
    const updated = { ...division, config: { ...(division.config || {}), format: divisionFormat } };
    updateDivision(updated, token, () => {
      toast.success('Format saved');
      setIsSavingFormat(false);
    });
  };

  // Standalone playoff bracket-size save (bracket / playoffs formats, which have no setup dialog).
  const handleSavePlayoffTeams = async (n: number) => {
    if (!division) return;
    const token = await getToken();
    const updated = { ...division, config: { ...(division.config || {}), playoffTeams: n } };
    updateDivision(updated, token, () => toast.success('Bracket size saved'));
  };

  // Fields to wipe when a format change / reconfigure invalidates generated
  // games — keeps the public Playoff Results view (playoffRound/placeholders)
  // in step with the cleared competitionMeta wiring.
  const clearedScheduleFields = (g: any) => ({
    competitionMeta: null,
    playoffRound: null,
    homeTeamPlaceholder: '',
    awayTeamPlaceholder: '',
    // Only wipe labels the bracket generator stamped, not custom ones.
    ...(g.competitionMeta?.bracketSlot ? { gameLabel: '' } : {}),
  });

  // Shared cleanup for every format change / reconfigure (owner 2026-10-09
  // bug fix): scoreless generator-stamped games are DELETED (they'd otherwise
  // orphan into the "unassigned" pile and accumulate on every re-Auto-Populate
  // — the reported 32-unassigned-games bug); scored games are kept but stripped
  // of schedule wiring; manual/unassigned games are left alone.
  const cleanupGamesForFormatChange = async () => {
    const { toDelete, toClear } = partitionFormatChangeGames(divisionGames);
    if (toDelete.length === 0 && toClear.length === 0) return;
    await Promise.all([
      ...toDelete.map((g) => deleteGame(g._id, { silent: true })),
      ...toClear.map((g) => updateGame({ ...g, ...clearedScheduleFields(g) }, () => {})),
    ]);
    await refreshGames();
  };

  const handleConfirmFormatChange = async () => {
    if (pendingFormat === null || !division) return;
    if (pendingFormat === 'pool-play' || pendingFormat === 'pool-play-playoffs') {
      setPoolSetupFormat(pendingFormat);
      setPoolSetupCount(2);
      setPoolSetupRoundsPerMatchup(1);
      setPoolSetupPlayoffTeams(_.get(division, 'config.playoffTeams', defaultPlayoffTeams(pendingFormat)));
      setPendingFormat(null);
      setPoolSetupOpen(true);
      return;
    }
    if (pendingFormat === 'round-robin' || pendingFormat === 'round-robin-playoffs') {
      setRoundRobinSetupFormat(pendingFormat);
      setRoundRobinSetupCount(1);
      setRoundRobinSetupPlayoffTeams(_.get(division, 'config.playoffTeams', defaultPlayoffTeams(pendingFormat)));
      setPendingFormat(null);
      setRoundRobinSetupOpen(true);
      return;
    }
    setIsSavingFormat(true);
    try {
      const token = await getToken();
      const nextConfig: any = { ...(division.config || {}), format: pendingFormat };
      if (pendingFormat === 'bracket' || pendingFormat === 'playoffs' || pendingFormat === 'playoffs-consolation') {
        // Consolation has no 2-team bracket, so a stored 2 coerces to 4.
        nextConfig.playoffTeams = normalizeBracketSize(
          division.config?.playoffTeams ?? defaultPlayoffTeams(pendingFormat),
          isConsolationFormat(pendingFormat),
        );
      }
      const updated = { ...division, config: nextConfig };
      await new Promise<void>((resolve) => updateDivision(updated, token, () => resolve()));
      setDivisionFormat(pendingFormat);
      await cleanupGamesForFormatChange();
      toast.success('Format saved');
    } finally {
      setPendingFormat(null);
      setIsSavingFormat(false);
    }
  };

  const reconfigurePools = useCallback(async (freshTeams: any[]) => {
    console.log('[Pool] reconfigurePools called: freshTeams=', freshTeams.length);
    // Read division live from the store to avoid stale closure issues
    const currentDivision = useDivisionStore.getState().divisions.find((d) => d._id === divisionId);
    console.log('[Pool] currentDivision from store:', currentDivision?._id, 'pools:', currentDivision?.config?.pools?.length);
    if (!currentDivision?.config?.pools?.length) {
      console.log('[Pool] reconfigurePools: early return — no pools in store division');
      return;
    }
    const numPools = currentDivision.config.pools.length;
    const token = await getToken();
    const sorted = [...freshTeams].sort(
      (a, b) => (isUnseededTeam(a.seed) ? Infinity : a.seed) - (isUnseededTeam(b.seed) ? Infinity : b.seed)
    );
    const distributed = distributeTeamsSerpentine(sorted, numPools);
    const pools = distributed.map((teams, i) => ({
      id: i + 1,
      name: currentDivision.config.pools[i]?.name ?? `Pool ${String.fromCharCode(65 + i)}`,
      teamIds: teams.map((t) => t._id),
    }));
    console.log('[Pool] new pools:', pools.map(p => ({ name: p.name, count: p.teamIds.length })));
    const updated = { ...currentDivision, config: { ...currentDivision.config, pools } };
    await new Promise<void>((resolve) => updateDivision(updated, token, () => resolve()));
    console.log('[Pool] updateDivision resolved');
  }, [divisionId, getToken, updateDivision]);

  const handleConfirmPoolSetup = async () => {
    if (!division || !poolSetupCount) return;
    setIsSavingFormat(true);
    try {
      const token = await getToken();
      const sorted = [...divisionTeams].sort((a, b) => (isUnseededTeam(a.seed) ? Infinity : a.seed) - (isUnseededTeam(b.seed) ? Infinity : b.seed));
      const distributed = distributeTeamsSerpentine(sorted, Number(poolSetupCount));
      const pools = distributed.map((teams, i) => ({
        id: i + 1,
        name: `Pool ${String.fromCharCode(65 + i)}`,
        teamIds: teams.map((t) => t._id),
      }));
      const poolConfig: any = { ...(division.config || {}), format: poolSetupFormat, pools, roundsPerMatchup: Number(poolSetupRoundsPerMatchup) || 1 };
      if (poolSetupFormat === 'pool-play-playoffs') poolConfig.playoffTeams = Number(poolSetupPlayoffTeams);
      const updated = { ...division, config: poolConfig };
      await new Promise<void>((resolve) => updateDivision(updated, token, () => resolve()));
      setDivisionFormat(poolSetupFormat);
      await cleanupGamesForFormatChange();
      toast.success('Pools configured');
    } finally {
      setPoolSetupOpen(false);
      setPoolSetupCount(2);
      setPoolSetupRoundsPerMatchup(1);
      setIsSavingFormat(false);
    }
  };

  const handleConfirmRoundRobinSetup = async () => {
    if (!division || !roundRobinSetupCount) return;
    setIsSavingFormat(true);
    try {
      const token = await getToken();
      const rrConfig: any = { ...(division.config || {}), format: roundRobinSetupFormat, roundsPerMatchup: Number(roundRobinSetupCount) };
      if (roundRobinSetupFormat === 'round-robin-playoffs') rrConfig.playoffTeams = Number(roundRobinSetupPlayoffTeams);
      const updated = { ...division, config: rrConfig };
      await new Promise<void>((resolve) => updateDivision(updated, token, () => resolve()));
      setDivisionFormat(roundRobinSetupFormat);
      await cleanupGamesForFormatChange();
      toast.success('Round Robin configured');
    } finally {
      setRoundRobinSetupOpen(false);
      setRoundRobinSetupCount(1);
      setIsSavingFormat(false);
    }
  };

  // ─── Seed sort ───────────────────────────────────────────────────────────────
  const handleEnterSeedSort = () => {
    const sorted = [...divisionTeams].sort((a, b) => (isUnseededTeam(a.seed) ? Infinity : a.seed) - (isUnseededTeam(b.seed) ? Infinity : b.seed));
    setSeedOrder(sorted.map((t) => t._id));
    setIsSeedDirty(false);
    setIsSeedSortMode(true);
  };

  const handleSeedDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSeedOrder((prev) => {
        const oldIdx = prev.indexOf(active.id as string);
        const newIdx = prev.indexOf(over.id as string);
        return arrayMove(prev, oldIdx, newIdx);
      });
      setIsSeedDirty(true);
    }
  };

  const handleSaveSeeds = async () => {
    setIsSavingSeeds(true);
    try {
      for (let i = 0; i < seedOrder.length; i++) {
        const team = divisionTeams.find((t) => t._id === seedOrder[i]);
        if (team) await updateTeamDivision({ ...team, seed: i + 1 }, undefined);
      }
      await getTeamDivisionsByDivisionId(divisionId);
      toast.success('Seeds saved');
      setIsSeedSortMode(false);
      setIsSeedDirty(false);
    } catch {
      toast.error('Failed to save seeds');
    } finally {
      setIsSavingSeeds(false);
    }
  };

  const handleCancelSeedSort = () => {
    setIsSeedSortMode(false);
    setIsSeedDirty(false);
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  // Rendered inside the Round Robin / Pool Play schedule sections (not the header).
  // Works with zero courts: generated games simply carry no location until an
  // admin assigns one via the edit dialog.
  const autoPopulateButton = (canAutoPopulate(divisionFormat) || canAutoPopulateBracket(divisionFormat)) ? (
    <Button
      variant="outline"
      size="sm"
      onClick={handleAutoPopulateClick}
      disabled={isAutoPopulating}
    >
      <ListChecks className="h-4 w-4 mr-2" />
      Auto-Populate Games
    </Button>
  ) : null;

  // Hybrid formats: generate the playoff bracket's placeholder games (one per
  // slot) independently of the group-stage games.
  const generatePlayoffBracketButton = isHybridPlayoffFormat ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleGeneratePlayoffBracketClick}
              disabled={isPlayoffPopulating}
            >
              <ListChecks className="h-4 w-4 mr-2" />
              Generate Playoff Bracket
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          Creates a placeholder game for every playoff slot ("Seed 1", "Winner of Semifinal 1", …).
          Assign teams once the group stage is decided.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null;

  return (
    <PageContainer fullWidth>
      <PageHeader
        title={division?.name || 'Division'}
        description={leagueName}
        actions={
          <>
          {/* Public-site counterpart of this division. Path shape lives in
              the shared publicPaths.ts (season|tournament segment — ADR 0009
              discriminator, NOT the org name); the domain comes from the
              shared domains.ts via publicSiteUrl. */}
          {orgName && leagueId && divisionId && (
            <Button variant="outline" asChild data-tour="division-view-on-site">
              <a
                href={publicSiteUrl(orgName, publicDivisionPath(division?.league as any, leagueId, divisionId))}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View this division on the public site"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View on Site
              </a>
            </Button>
          )}
          {hasScoresheet(divisionFormat) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Download Scoresheet
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {(SCORESHEET_VARIANTS[divisionFormat] || []).map((v) => (
                  <DropdownMenuItem
                    key={v.id}
                    onClick={() => {
                      try {
                        generateScoresheet(divisionFormat, v.id, {
                          division,
                          teams: divisionTeams,
                          leagueName,
                        });
                      } catch (err) {
                        console.error('scoresheet generation failed', err);
                        toast.error('Could not generate scoresheet');
                      }
                    }}
                  >
                    {v.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {scanSupported && sport === 'pickleball' && canAutoPopulate(divisionFormat) && (
            <Button variant="outline" onClick={() => setScan({ type: 'pickleball' })}>
              <Camera className="h-4 w-4 mr-2" />
              Scan Scoresheet
            </Button>
          )}
          {organization?.sports?.[0] === 'basketball' && (
            <Button variant="outline" onClick={openScoresheetPreviews}>
              <Download className="h-4 w-4 mr-2" />
              Download Scoresheet
            </Button>
          )}
          <Button variant="outline" onClick={() => setIsScheduleUploadOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Schedule
          </Button>
          {divisionFormat !== 'pool-play' && divisionFormat !== 'pool-play-playoffs' && (
            <Button onClick={openCreateModal}>
              <Plus className="h-4 w-4 mr-2" />
              Create Game
            </Button>
          )}
          </>
        }
      />

      <TeamList
        teams={divisionTeams}
        pendingTeamIds={pendingTeamIds}
        divisionGames={divisionGames}
        onAddTeam={openAddTeamDialog}
        isSeedSortMode={isSeedSortMode}
        seedOrder={seedOrder}
        onEnterSeedSort={handleEnterSeedSort}
        onDragEnd={handleSeedDragEnd}
        isSeedDirty={isSeedDirty}
        isSavingSeeds={isSavingSeeds}
        onSaveSeeds={handleSaveSeeds}
        onCancelSeedSort={handleCancelSeedSort}
      />

      <Card>
        <CardHeader>
          <CardTitle>Division Format</CardTitle>
        </CardHeader>
        <CardContent>
          <TooltipProvider>
            <div className="flex flex-wrap gap-2">
              {(isTournamentDivision ? TOURNAMENT_FORMATS : REGULAR_FORMATS).map(({ value, label, description, minTeams, tooltip }) => {
                const isDisabled = numTeams < minTeams;
                const isSelected = divisionFormat === value;
                return (
                  <Tooltip key={value}>
                    <TooltipTrigger asChild>
                      <button
                        aria-disabled={isDisabled || isSelected}
                        onClick={() => {
                          if (isDisabled || isSelected) return;
                          setPendingFormat(value);
                        }}
                        className={`text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                          isSelected
                            ? 'border-primary bg-primary/10 cursor-default'
                            : isDisabled
                              ? 'border-border bg-muted/50 opacity-50 cursor-not-allowed'
                              : 'border-border bg-card hover:border-primary/50 hover:bg-primary/10 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${isSelected ? 'text-primary' : isDisabled ? 'text-muted-foreground/60' : 'text-foreground'}`}>
                            {label}
                          </span>
                          {isSelected && <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">Active</span>}
                          {isDisabled && <span className="text-xs text-muted-foreground/60">Requires {minTeams}+ teams</span>}
                        </div>
                        <p className={`text-xs mt-0.5 ${isDisabled ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>{description}</p>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>

          {/* Playoff Bracket Size — a subsection of Division Format (owner
              2026-10-09): only shown for bracket/playoff formats, since it's a
              property of that chosen format. */}
          {isBracketFormat && (
            <div className="mt-5 border-t border-border pt-4">
              <div className="text-sm font-medium text-foreground">Playoff Bracket Size</div>
              <p className="mb-2 text-xs text-muted-foreground">
                Choose how many teams play in the bracket. This drives the schedule below and the downloadable scoresheet.
              </p>
              <div className="max-w-xs space-y-2">
                <Select value={String(bracketSize)} onValueChange={(v) => handleSavePlayoffTeams(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLAYOFF_SIZE_OPTIONS.filter((o) => !(isBracketConsolation && o.value === 2)).map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.value} teams ({o.label})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {bracketSize > numTeams && (
                  <p className="text-xs text-warning">Only {numTeams} team{numTeams !== 1 ? 's' : ''}. Some bracket slots will be empty.</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View toggle (owner 2026-10-09): switch the scheduling surface below
          between the drag-drop grid and a spreadsheet for bulk score entry.
          iOS-style segmented control (owner 2026-10-09) — the active view is a
          raised white chip on a grey track, so it's unmistakable which is on. */}
      <div className="flex items-center gap-2">
        <div className="inline-flex items-center gap-0.5 rounded-lg bg-muted p-1" role="group" aria-label="Choose view">
          {([
            { v: 'schedule', label: 'Schedule', Icon: LayoutGrid },
            { v: 'table', label: 'Table', Icon: Table2 },
            { v: 'standings', label: 'Standings', Icon: ListOrdered },
          ] as const).map(({ v, label, Icon }) => {
            const active = viewMode === v;
            return (
              <button
                key={v}
                type="button"
                aria-pressed={active}
                onClick={() => setViewMode(v)}
                className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm transition-colors ${
                  active
                    ? 'bg-background font-semibold text-foreground shadow-sm ring-1 ring-black/5'
                    : 'font-medium text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Standings (INT-0055, owner 2026-10-30): the public standings table in
          the admin. Server-resolved effectiveConfig first (same source as the
          public page, ADR 0030); the client getConfig only for a division
          fetched before the annotation. Sport through the shared resolver so
          the order matches the public page and bracket advancement (ADR 0029). */}
      {viewMode === 'standings' && (
        <DivisionStandings
          divisionGames={divisionGames}
          divisionTeams={divisionTeams}
          config={(division as any)?.effectiveConfig || effectiveConfig}
          sport={resolveStandingsSport({ division, league, tournament, organization })}
          publicStandingsUrl={orgName && leagueId && divisionId
            ? publicSiteUrl(orgName, publicDivisionStandingsPath(division?.league as any, leagueId, divisionId))
            : null}
        />
      )}

      {viewMode === 'table' && (
        <GamesScoreTable
          divisionGames={divisionGames}
          divisionTeams={divisionTeams}
          updateGame={updateGame}
          refreshGames={refreshGames}
          onEditGame={openEditModal}
        />
      )}

      {viewMode === 'schedule' && (<>
      {isBracketFormat && (
        <>
          <div className="flex justify-end gap-2">
            {autoPopulateButton}
          </div>
          <BracketScheduler
            divisionGames={divisionGames}
            divisionTeams={divisionTeams}
            format={divisionFormat}
            playoffTeams={bracketSize}
            updateGame={updateGame}
            refreshGames={refreshGames}
            onEditGame={openEditModal}
          />
        </>
      )}

      {(divisionFormat === 'round-robin' || divisionFormat === 'round-robin-playoffs') && (
        <>
          <div className="flex justify-end gap-2">
            {autoPopulateButton}
            {generatePlayoffBracketButton}
            <Button size="sm" variant="outline" onClick={() => {
              setRoundRobinSetupFormat(divisionFormat);
              setRoundRobinSetupCount(_.get(division, 'config.roundsPerMatchup', 1));
              setRoundRobinSetupPlayoffTeams(_.get(division, 'config.playoffTeams', defaultPlayoffTeams(divisionFormat)));
              setRoundRobinSetupOpen(true);
            }}>
              Reconfigure Round Robin
            </Button>
          </div>
          {numTeams >= 2 && (
            <RoundRobinScheduler
              divisionGames={divisionGames}
              divisionTeams={divisionTeams}
              numTeams={numTeams}
              numRounds={numRounds}
              gamesPerRound={gamesPerRound}
              isEvenTeams={isEvenTeams}
              isPlayoffs={divisionFormat === 'round-robin-playoffs'}
              playoffTeams={_.get(division, 'config.playoffTeams', defaultPlayoffTeams('round-robin-playoffs'))}
              updateGame={updateGame}
              refreshGames={refreshGames}
              onEditGame={openEditModal}
            />
          )}
        </>
      )}

      {(divisionFormat === 'pool-play' || divisionFormat === 'pool-play-playoffs') && (() => {
        const hasPools = division?.config?.pools && division.config.pools.length > 0;
        return (
          <>
            {hasPools ? (
              <PoolPlayScheduler
                divisionGames={divisionGames}
                divisionTeams={divisionTeams}
                pools={division.config.pools}
                updateGame={updateGame}
                refreshGames={refreshGames}
                onEditGame={openEditModal}
                onCreateGameForPool={openCreateModalForPool}
                canCreateGame={true}
                onEditAssignments={openEditPoolAssignments}
                onReassignTeam={handleReassignTeam}
                roundsPerMatchup={_.get(division, 'config.roundsPerMatchup', 1) || 1}
              />
            ) : (
              <Card>
                <CardContent className="py-6 text-center space-y-3">
                  <p className="text-sm text-muted-foreground">Pools have not been set up yet.</p>
                  <Button size="sm" onClick={() => { setPoolSetupFormat(divisionFormat); setPoolSetupCount(2); setPoolSetupRoundsPerMatchup(1); setPoolSetupOpen(true); }}>
                    Set Up Pools
                  </Button>
                </CardContent>
              </Card>
            )}
            {hasPools && (
              <div className="flex justify-end gap-2">
                {autoPopulateButton}
                {generatePlayoffBracketButton}
                <Button size="sm" variant="outline" onClick={() => { setPoolSetupFormat(divisionFormat); setPoolSetupCount(division.config.pools.length); setPoolSetupRoundsPerMatchup(_.get(division, 'config.roundsPerMatchup', 1)); setPoolSetupOpen(true); }}>
                  Reconfigure Pools
                </Button>
              </div>
            )}
          </>
        );
      })()}

      {unscoredDivisionGames.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Enter Results</CardTitle>
            <CardDescription>
              Games still needing scores.{scanSupported ? ' Scan a scoresheet photo or key scores directly.' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {unscoredDivisionGames.map((g) => (
              <GameResultRow
                key={g._id}
                game={g}
                sport={sport}
                supported={scanSupported}
                onScanBasketball={(game) => setScan({ type: 'basketball', game })}
                onSaved={refreshGames}
              />
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Date</CardTitle>
        </CardHeader>
        <CardContent>
          {allDateKeys.length === 0 ? (
            <p className="text-sm text-muted-foreground">No games yet. Hit Create Game to schedule your first. (Add court availability in Courts to unlock the drag-and-drop day grid.)</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {allDateKeys.map((key) => (
                <Button
                  key={key}
                  type="button"
                  variant={key === selectedDateKey ? 'default' : 'outline'}
                  onClick={() => { setSelectedDateKey(key); setGridStartOverride(null); }}
                  className="h-auto py-2 px-3 text-left"
                >
                  {formatDateForSelect(key)}
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {locations.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground mb-3">Nothing on the grid for this date yet. Create a game above, or add court availability to unlock drag-and-drop scheduling and conflict checks.</p>
            <Link to="/admin/locations" className="text-primary underline">Add court availability</Link>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1.5">
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="h-5 w-5" />
                  Game Schedule
                </CardTitle>
                <CardDescription>Drag game cards vertically to move start time. Drag bottom handle to resize in 15-minute increments.</CardDescription>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">Grid starts</span>
                <Select
                  value={gridStartOverride == null ? 'auto' : String(gridStartOverride)}
                  onValueChange={(v) => setGridStartOverride(v === 'auto' ? null : Number(v))}
                >
                  <SelectTrigger className="h-8 w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (1h before first game)</SelectItem>
                    {[5, 6, 7, 8, 9, 10, 11, 12].map((hour) => (
                      <SelectItem key={hour} value={String(hour * 60)}>
                        {formatTimeLabel(hour * 60)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <div ref={scheduleMeasureRef} className="w-full">
            <div
              ref={calendarRef}
              data-testid="division-day-grid"
              className="relative min-w-max border rounded-md bg-card"
              style={{ width: TIME_GUTTER_WIDTH + locations.length * columnWidth }}
            >
              {/* Header row */}
              <div className="sticky top-0 z-30 flex border-b bg-card">
                <div style={{ width: TIME_GUTTER_WIDTH }} className="border-r px-2 py-2 text-xs font-semibold text-muted-foreground">
                  Time
                </div>
                {locations.map((location) => (
                  <div
                    key={location._id}
                    style={{ width: columnWidth }}
                    className="border-r px-3 py-2 text-sm font-semibold"
                  >
                    {location.synthetic ? location.name : getLocationDisplayName(location)}
                  </div>
                ))}
              </div>

              {/* Body */}
              <div className="relative flex">
                {/* Time gutter */}
                <div style={{ width: TIME_GUTTER_WIDTH, height: totalHeight }} className="border-r bg-muted/50">
                  {quarterTicks.map((minute) => {
                    const top = ((minute - bounds.start) / SNAP_MINUTES) * SLOT_HEIGHT;
                    const isHour = minute % 60 === 0;
                    return (
                      <div key={`label-${minute}`} className="absolute left-0 right-0" style={{ top }}>
                        {isHour && (
                          <span className="absolute -top-2 left-2 text-[11px] text-muted-foreground">
                            {formatTimeLabel(minute)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Location columns */}
                {locations.map((location) => {
                  const slotRanges = timeSlotRangesByLocation[location._id] || [];
                  const layout = layoutByLocation[location._id] || { items: [], groups: [] };

                  return (
                    <div
                      key={location._id}
                      style={{ width: columnWidth, height: totalHeight }}
                      className="relative border-r"
                    >
                      {/* Grid lines */}
                      {quarterTicks.map((minute) => {
                        const top = ((minute - bounds.start) / SNAP_MINUTES) * SLOT_HEIGHT;
                        const isHour = minute % 60 === 0;
                        return (
                          <div
                            key={`line-${location._id}-${minute}`}
                            className={`absolute left-0 right-0 border-t ${isHour ? 'border-input' : 'border-border'}`}
                            style={{ top }}
                          />
                        );
                      })}

                      {/* Available time slot ranges */}
                      {slotRanges.map((range, index) => {
                        const start = clamp(range.start, bounds.start, bounds.end);
                        const end = clamp(range.end, bounds.start, bounds.end);
                        if (end <= start) return null;
                        const top = ((start - bounds.start) / SNAP_MINUTES) * SLOT_HEIGHT;
                        const height = ((end - start) / SNAP_MINUTES) * SLOT_HEIGHT;
                        return (
                          <div
                            key={`range-${location._id}-${index}`}
                            className="absolute left-0 right-0 bg-success/10 pointer-events-none"
                            style={{ top, height }}
                          />
                        );
                      })}

                      {/* Hidden count badges */}
                      {layout.groups.map((group) => {
                        if (!group.hiddenCount) return null;
                        const top = ((group.start - bounds.start) / SNAP_MINUTES) * SLOT_HEIGHT;
                        return (
                          <div
                            key={`more-${location._id}-${group.start}`}
                            className="absolute right-2 z-20 rounded-full bg-foreground/80 px-2 py-[2px] text-[10px] font-semibold text-background"
                            style={{ top: top + 2 }}
                          >
                            +{group.hiddenCount} more
                          </div>
                        );
                      })}

                      {/* Game cards */}
                      {layout.items.map((item) => {
                        if (item.column >= maxVisibleColumns) return null;

                        const game = item.game;
                        const gameId = game._id;
                        const start = item.start;
                        const duration = item.duration;
                        const top = ((start - bounds.start) / SNAP_MINUTES) * SLOT_HEIGHT;
                        const height = Math.max((duration / SNAP_MINUTES) * SLOT_HEIGHT, SLOT_HEIGHT);

                        const visibleColumns = Math.min(item.columnCount || 1, maxVisibleColumns);
                        const availableWidth = columnWidth - GAME_INSET * 2 - (visibleColumns - 1) * GAME_GAP;
                        const cardWidth = Math.max(40, availableWidth / visibleColumns);
                        const left = GAME_INSET + item.column * (cardWidth + GAME_GAP);

                        const home = getTeamName(game.homeTeam, teamDivisions, game.homeTeamPlaceholder);
                        const away = getTeamName(game.awayTeam, teamDivisions, game.awayTeamPlaceholder);

                        return (
                          <div
                            key={gameId}
                            className="absolute rounded-md shadow-sm overflow-hidden border border-primary bg-primary/10"
                            style={{ top, height, minHeight: SLOT_HEIGHT, left, width: cardWidth }}
                          >
                            <button
                              type="button"
                              className="w-full h-full px-2 pt-1 pb-4 text-left cursor-grab active:cursor-grabbing"
                              onMouseDown={(event) => onStartMove(event, game)}
                            >
                              <div className="text-[11px] font-semibold text-primary-deep truncate">
                                {formatMinutesForGameTime(start)} - {formatMinutesForGameTime(start + duration)}
                              </div>
                              <div className="text-[11px] text-foreground truncate">{home} vs {away}</div>
                              {game.homeScore != null && game.awayScore != null && (
                                <div className="text-[11px] font-semibold text-foreground truncate">
                                  {game.homeScore} - {game.awayScore}
                                </div>
                              )}
                            </button>
                            <button
                              type="button"
                              className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize bg-primary/40 hover:bg-primary/70"
                              onMouseDown={(event) => onStartResize(event, game)}
                              aria-label="Resize game duration"
                            />
                            <button
                              type="button"
                              title="Edit game"
                              className="absolute top-0 right-5 z-10 p-1 text-primary/60 hover:text-primary"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => { e.stopPropagation(); openEditModal(game); }}
                            >
                              <Pencil size={12} />
                            </button>
                            <Link
                              to={`/admin/game/${gameId}/stats`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View stats"
                              className="absolute top-0 right-0 z-10 p-1 text-primary/60 hover:text-primary"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <BarChart3 size={12} />
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
            </div>
          </CardContent>
        </Card>
      )}
      </>)}

      {division && leagueName && (
        <DivisionLayoutEditor
          divisionId={divisionId}
          divisionName={division.name}
          organizationId={helpers.getObjId(division.organization)}
          leagueId={leagueId}
          leagueName={leagueName}
        />
      )}

      {/* Edit pool assignments modal */}
      <Dialog open={editPoolsOpen} onOpenChange={(open) => { if (!open) setEditPoolsOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Pool Assignments</DialogTitle>
            <DialogDescription>Reassign teams to different pools.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {divisionTeams.map((team) => (
              <div key={team._id} className="flex items-center justify-between gap-3">
                <span className="text-sm truncate flex-1">{getTeamDisplayName(team)}</span>
                <Select
                  value={String(editPoolAssignments[team._id] ?? '')}
                  onValueChange={(v) => setEditPoolAssignments((prev) => ({ ...prev, [team._id]: Number(v) }))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Select pool" />
                  </SelectTrigger>
                  <SelectContent>
                    {division?.config?.pools?.map((pool) => (
                      <SelectItem key={pool.id} value={String(pool.id)}>{pool.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPoolsOpen(false)} disabled={isSavingPoolAssignments}>Cancel</Button>
            <Button onClick={handleSavePoolAssignments} disabled={isSavingPoolAssignments}>
              {isSavingPoolAssignments ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pool setup modal */}
      <Dialog open={poolSetupOpen} onOpenChange={(open) => { if (!open) setPoolSetupOpen(false); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Set Up Pools</DialogTitle>
            <DialogDescription>{numTeams} teams will be distributed across pools.</DialogDescription>
          </DialogHeader>
          {(() => {
            const count = Number(poolSetupCount) || 0;
            const isValid = count >= 2 && numTeams > 0 && Math.floor(numTeams / count) >= 3;
            const isUneven = count > 0 && numTeams % count !== 0;
            const teamsPerPool = count > 0 ? numTeams / count : 0;
            const isSeeded = divisionTeams.some((t) => !isUnseededTeam(t.seed));
            const sorted = [...divisionTeams].sort((a, b) => (isUnseededTeam(a.seed) ? Infinity : a.seed) - (isUnseededTeam(b.seed) ? Infinity : b.seed));
            const preview = count >= 2 && isValid ? distributeTeamsSerpentine(sorted, count) : [];
            return (
              <>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>Number of Pools</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={poolSetupCount}
                      onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setPoolSetupCount(v === '' ? '' : Number(v)); }}
                    />
                    {count >= 2 && !isValid && (
                      <p className="text-xs text-destructive">
                        Each pool needs at least 3 teams. With {numTeams} teams, max {Math.floor(numTeams / 3)} pool{Math.floor(numTeams / 3) !== 1 ? 's' : ''}.
                      </p>
                    )}
                    {isUneven && isValid && (
                      <p className="text-xs text-warning">
                        Teams cannot be evenly distributed ({numTeams} teams across {count} pools).
                        Some pools will have {Math.ceil(teamsPerPool)} teams, others {Math.floor(teamsPerPool)}.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Times each team plays another team</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={poolSetupRoundsPerMatchup}
                      onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setPoolSetupRoundsPerMatchup(v === '' ? '' : Math.min(8, Math.max(1, Number(v)))); }}
                    />
                  </div>
                  {poolSetupFormat === 'pool-play-playoffs' && (
                    <div className="space-y-2">
                      <Label>Playoff bracket size</Label>
                      <Select value={String(poolSetupPlayoffTeams)} onValueChange={(v) => setPoolSetupPlayoffTeams(Number(v))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PLAYOFF_SIZE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={String(o.value)}>{o.value} teams ({o.label})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {poolSetupPlayoffTeams > numTeams && (
                        <p className="text-xs text-warning">Only {numTeams} team{numTeams !== 1 ? 's' : ''}. Some bracket slots will be empty.</p>
                      )}
                    </div>
                  )}
                  {isValid && preview.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-xs font-medium text-muted-foreground">
                          Preview {isSeeded ? '(serpentine seeding)' : '(serpentine seeding, unseeded teams placed last)'}
                        </p>
                        <p className="text-xs text-muted-foreground/60 shrink-0">You can reconfigure pools later</p>
                      </div>
                      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(count, 4)}, 1fr)` }}>
                        {preview.map((poolTeams, i) => (
                          <div key={i} className="border rounded-md p-2">
                            <div className="text-xs font-semibold text-foreground mb-1">
                              Pool {String.fromCharCode(65 + i)} ({poolTeams.length})
                            </div>
                            {poolTeams.map((t) => (
                              <div key={t._id} className="text-xs text-muted-foreground truncate">
                                {!isUnseededTeam(t.seed) ? <span className="text-muted-foreground/60">#{t.seed} </span> : null}{getTeamDisplayName(t)}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPoolSetupOpen(false)} disabled={isSavingFormat}>Cancel</Button>
                  <Button onClick={handleConfirmPoolSetup} disabled={!isValid || isSavingFormat}>
                    {isSavingFormat ? 'Setting up…' : 'Confirm'}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Round Robin setup modal */}
      <Dialog open={roundRobinSetupOpen} onOpenChange={(open) => { if (!open) setRoundRobinSetupOpen(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Set Up Round Robin</DialogTitle>
            <DialogDescription>{numTeams} teams will play each other the specified number of times.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Times each team plays another team</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={roundRobinSetupCount}
                onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setRoundRobinSetupCount(v === '' ? '' : Math.min(8, Math.max(1, Number(v)))); }}
              />
              <p className="text-sm text-muted-foreground">
                {numTeams >= 2 && roundRobinSetupCount
                  ? (() => {
                      const n = numTeams;
                      const even = n % 2 === 0;
                      const rounds = (even ? n - 1 : n) * Number(roundRobinSetupCount);
                      const gpr = even ? n / 2 : Math.floor(n / 2);
                      return `${rounds} rounds · ${gpr * rounds} total games`;
                    })()
                  : 'Add teams to see totals'}
              </p>
            </div>
            {roundRobinSetupFormat === 'round-robin-playoffs' && (
              <div className="space-y-2">
                <Label>Playoff bracket size</Label>
                <Select value={String(roundRobinSetupPlayoffTeams)} onValueChange={(v) => setRoundRobinSetupPlayoffTeams(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLAYOFF_SIZE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.value} teams ({o.label})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {roundRobinSetupPlayoffTeams > numTeams && (
                  <p className="text-xs text-warning">Only {numTeams} team{numTeams !== 1 ? 's' : ''}. Some bracket slots will be empty.</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoundRobinSetupOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmRoundRobinSetup} disabled={!roundRobinSetupCount || isSavingFormat}>
              {isSavingFormat ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change format confirmation dialog */}
      <Dialog open={pendingFormat !== null} onOpenChange={(open) => { if (!open) setPendingFormat(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Division Format?</DialogTitle>
            <DialogDescription>
              Changing the format clears the current schedule. Games <strong>without a score are removed</strong>; any game <strong>with a score is kept</strong> but unassigned from the schedule.{' '}
              {(() => {
                const { toDelete, toClear } = partitionFormatChangeGames(divisionGames);
                if (toDelete.length === 0 && toClear.length === 0) {
                  return 'No scheduled games to change.';
                }
                const parts = [];
                if (toDelete.length) parts.push(`${toDelete.length} game${toDelete.length !== 1 ? 's' : ''} will be removed`);
                if (toClear.length) parts.push(`${toClear.length} scored game${toClear.length !== 1 ? 's' : ''} kept`);
                return <strong>{parts.join(', ')}.</strong>;
              })()}
              {' '}This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingFormat(null)} disabled={isSavingFormat}>
              Cancel
            </Button>
            <Button onClick={handleConfirmFormatChange} disabled={isSavingFormat}>
              {isSavingFormat ? 'Saving…' : 'Confirm Change'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scoresheet scan dialogs (shared with /admin/game-results) */}
      <ScoresheetScanDialogs
        scan={scan}
        division={division}
        org={organization}
        onClose={() => setScan(null)}
        onSubmitted={refreshGames}
      />

      {/* Auto-populate replace-confirm dialog */}
      <Dialog open={autoPopulateOpen} onOpenChange={(open) => { if (!open) setAutoPopulateOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace generated games?</DialogTitle>
            <DialogDescription>
              This division already has <strong>{autoPopulateReplaceTargets.length} auto-generated game{autoPopulateReplaceTargets.length !== 1 ? 's' : ''}</strong>.
              Continuing will delete {autoPopulateReplaceTargets.length !== 1 ? 'them' : 'it'} and create{' '}
              <strong>
                {(canAutoPopulateBracket(divisionFormat) ? autoPopulateBracketGames.length : autoPopulateMatchups.length)} new game
                {(canAutoPopulateBracket(divisionFormat) ? autoPopulateBracketGames.length : autoPopulateMatchups.length) !== 1 ? 's' : ''}
              </strong>{' '}
              on {selectedDateKey || 'the selected date'} at 9:00 AM{selectableLocations.length > 0 ? ', spread across your courts' : ''}. This cannot be undone.
              {autoPopulateReplaceTargets.some((g: any) => !isParseableTime(g?.time)) ? ' This includes the schedule drafted during sign-up.' : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoPopulateOpen(false)} disabled={isAutoPopulating}>
              Cancel
            </Button>
            <Button onClick={runAutoPopulate} disabled={isAutoPopulating}>
              {isAutoPopulating ? 'Working…' : 'Replace & Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate-playoff-bracket replace-confirm dialog (hybrid formats) */}
      <Dialog open={playoffPopulateOpen} onOpenChange={(open) => { if (!open) setPlayoffPopulateOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace playoff bracket games?</DialogTitle>
            <DialogDescription>
              This division already has <strong>{generatedBracketGames.length} playoff bracket game{generatedBracketGames.length !== 1 ? 's' : ''}</strong>.
              Continuing will delete {generatedBracketGames.length !== 1 ? 'them' : 'it'} (including any assigned teams and scores) and create{' '}
              <strong>{hybridPlayoffBracketGames.length} placeholder game{hybridPlayoffBracketGames.length !== 1 ? 's' : ''}</strong>.
              Group-stage games are not affected. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlayoffPopulateOpen(false)} disabled={isPlayoffPopulating}>
              Cancel
            </Button>
            <Button onClick={runGeneratePlayoffBracket} disabled={isPlayoffPopulating}>
              {isPlayoffPopulating ? 'Working…' : 'Replace & Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Basketball scoresheet previews — click a preview to download it */}
      <Dialog open={scoresheetOpen} onOpenChange={(open) => { if (!open) closeScoresheetPreviews(); }}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>Download Scoresheet</DialogTitle>
            <DialogDescription>
              Click a scoresheet to download it. Print copies for scorekeepers to fill in during games.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {scoresheetPreviews.map((p) => (
              <button
                key={p.variantId}
                type="button"
                onClick={() => saveDoc(p.doc, p.filename)}
                title={`Download ${p.label}`}
                className="group flex flex-col overflow-hidden rounded-lg border text-left transition-all hover:border-primary hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {/* Match the sheet's landscape-letter aspect (11 x 8.5) so the
                    preview fills the box with no empty PDF-viewer background. */}
                <div className="relative aspect-[11/8.5] overflow-hidden bg-white">
                  {/* pointer-events-none so the whole card is the click target */}
                  <iframe
                    src={`${p.url}#toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
                    title={`${p.label} preview`}
                    className="pointer-events-none h-full w-full"
                  />
                  <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-black/50 to-transparent opacity-0 transition-opacity group-hover:opacity-100">
                    <span className="mb-4 inline-flex items-center gap-1 rounded-full bg-card px-3 py-1.5 text-sm font-medium text-foreground shadow">
                      <Download className="h-4 w-4" /> Download
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between border-t px-3 py-2">
                  <span className="text-sm font-medium">{p.label}</span>
                  <Download className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeScoresheetPreviews}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* INT-0026 — AI schedule import (upload → editable preview → commit) */}
      <ScheduleUploadDialog
        open={isScheduleUploadOpen}
        onOpenChange={setIsScheduleUploadOpen}
        divisionId={divisionId}
        divisionName={division?.name}
        onImported={() => {
          getTeamDivisionsByDivisionId(divisionId);
          getGamesByDivisionId(divisionId);
        }}
      />

      {/* Create game dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Game</DialogTitle>
            <DialogDescription>
              Set teams, date, location and time for {formatDateForSelect(createForm.dateKey || selectedDateKey)}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Home Team</Label>
                <Select
                  value={createForm.homeTeamId}
                  onValueChange={(value) => setCreateForm((prev) => ({ ...prev, homeTeamId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select home team" />
                  </SelectTrigger>
                  <SelectContent>
                    {(createFormPoolTeams.length > 0 ? createFormPoolTeams : divisionTeams).map((team) => (
                      <SelectItem key={team._id} value={team._id}>{getTeamDisplayName(team)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Away Team</Label>
                <Select
                  value={createForm.awayTeamId}
                  onValueChange={(value) => setCreateForm((prev) => ({ ...prev, awayTeamId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select away team" />
                  </SelectTrigger>
                  <SelectContent>
                    {(createFormPoolTeams.length > 0 ? createFormPoolTeams : divisionTeams).map((team) => (
                      <SelectItem key={team._id} value={team._id}>{getTeamDisplayName(team)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={createForm.dateKey || selectedDateKey}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, dateKey: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Location <span className="font-normal text-muted-foreground">(optional)</span></Label>
              {/* INT-0027: combobox with create-on-type; location optional. */}
              <LocationCombobox
                locations={selectableLocations.map((l: any) => ({ ...l, displayName: getLocationDisplayName(l) }))}
                value={createForm.locationId}
                onSelect={(id) => setCreateForm((prev) => ({ ...prev, locationId: id }))}
                onCreate={async (name) => {
                  const doc = await createLocation({ name, organization: helpers.getObjId(division?.organization) });
                  if (doc) await getLocationsByLeagueName(orgName, { includeSubVenues: true });
                  return doc;
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Select
                  value={String(createForm.startMinutes)}
                  onValueChange={(value) => setCreateForm((prev) => ({ ...prev, startMinutes: Number(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select start time" />
                  </SelectTrigger>
                  <SelectContent>
                    {quarterTicks.map((minute) => (
                      <SelectItem key={`start-${minute}`} value={String(minute)}>
                        {formatMinutesForGameTime(minute)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Duration</Label>
                <Select
                  value={String(createForm.duration)}
                  onValueChange={(value) => setCreateForm((prev) => ({ ...prev, duration: Number(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {[15, 30, 45, 60, 75, 90, 105, 120].map((value) => (
                      <SelectItem key={`dur-${value}`} value={String(value)}>{value} minutes</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Home Score</Label>
                <Input type="text" inputMode="numeric" placeholder="—" value={createForm.homeScore}
                  onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setCreateForm((prev) => ({ ...prev, homeScore: v === '' ? '' : Number(v) })); }} />
              </div>
              <div className="space-y-2">
                <Label>Away Score</Label>
                <Input type="text" inputMode="numeric" placeholder="—" value={createForm.awayScore}
                  onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setCreateForm((prev) => ({ ...prev, awayScore: v === '' ? '' : Number(v) })); }} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button onClick={onCreateGame}>Create Game</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add team dialog */}
      <Dialog open={isAddTeamOpen} onOpenChange={setIsAddTeamOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Team</DialogTitle>
            <DialogDescription>
              {registration?._id
                ? (addTeamForm.forcePlace
                  ? 'The team will be registered, marked accepted, and shown on the public division page immediately.'
                  : addTeamForm.sendInvite
                    ? 'The team will be registered and the manager emailed a link to complete registration and payment. It stays off the public page until accepted.'
                    : 'The team will be linked to this registration ("Registered" badge) and stays off the public page until accepted.')
                : 'This division has no registration. The team is added directly and appears with the "Unregistered" badge.'}
            </DialogDescription>
          </DialogHeader>

          {isRegistrationLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Loading registration fields…</p>
          ) : (
            <div className="space-y-6">
              {/* Team Name — excluded entirely for excludeTeamName formats
                  (the team is identified by its players; the name is auto-generated). */}
              {!excludeTeamName && (
                <div className="space-y-2">
                  <Label htmlFor="add-team-name">
                    Team Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="add-team-name"
                    value={addTeamForm.teamName}
                    onChange={(e) => setAddTeamForm((prev) => ({ ...prev, teamName: e.target.value }))}
                    placeholder="Enter team name"
                    className={addTeamValidation.teamNameMissing ? 'border-destructive focus-visible:ring-destructive' : ''}
                  />
                  {addTeamValidation.teamNameMissing && (
                    <p className="text-[11px] text-destructive">Required</p>
                  )}
                </div>
              )}

              {/* Players */}
              {collectRoster && (() => {
                const activePlayerFields = (registration?.fields || [])
                  .filter((f: any) => f.include !== false)
                  .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
                return (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-1">
                      <h3 className="text-sm font-semibold text-foreground">
                        Players
                        {effectiveConfig.minPlayers > 0 && (
                          <span className="ml-1 text-xs font-normal text-muted-foreground">
                            (min {effectiveConfig.minPlayers}{effectiveConfig.maxPlayers ? `, max ${effectiveConfig.maxPlayers}` : ''})
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center gap-2">
                        {effectiveConfig.maxPlayers > 0 && addTeamForm.players.length >= effectiveConfig.maxPlayers && (
                          <span className="text-xs text-muted-foreground">Max reached</span>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={effectiveConfig.maxPlayers > 0 && addTeamForm.players.length >= effectiveConfig.maxPlayers}
                          onClick={() => setAddTeamForm((prev) => ({
                            ...prev,
                            players: [...prev.players, { info: {} }],
                          }))}
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add Player
                        </Button>
                      </div>
                    </div>
                    {addTeamForm.players.map((player, idx) => {
                      const playerMissing = addTeamValidation.missingPlayerFields[idx] || new Set<string>();
                      return (
                      <div key={idx} className="border rounded-md p-3 space-y-3 relative">
                        <button
                          type="button"
                          className="absolute top-2 right-2 text-muted-foreground/60 hover:text-destructive"
                          onClick={() => setAddTeamForm((prev) => ({ ...prev, players: prev.players.filter((_, i) => i !== idx) }))}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <p className="text-xs font-medium text-muted-foreground">Player {idx + 1}</p>
                        {activePlayerFields.length > 0 ? (
                          activePlayerFields.map((field: any) => {
                            const missing = playerMissing.has(field.fieldId);
                            return (
                              <div key={field.fieldId} className="space-y-1">
                                <Label>
                                  {field.name}
                                  {field.required && <span className="text-destructive ml-1">*</span>}
                                </Label>
                                {field.helperText && <p className="text-xs text-muted-foreground">{field.helperText}</p>}
                                <div className={missing ? 'rounded-md ring-1 ring-destructive' : ''}>
                                  <RegistrationFieldInput
                                    field={field}
                                    value={player.info[field.fieldId]}
                                    onChange={(v) => setAddTeamForm((prev) => {
                                      const players = [...prev.players];
                                      players[idx] = { ...players[idx], info: { ...players[idx].info, [field.fieldId]: v } };
                                      return { ...prev, players };
                                    })}
                                  />
                                </div>
                                {missing && <p className="text-[11px] text-destructive">Required</p>}
                              </div>
                            );
                          })
                        ) : (
                          (() => {
                            const missing = playerMissing.has('name');
                            return (
                              <div className="space-y-1">
                                <Label>Name <span className="text-destructive">*</span></Label>
                                <Input
                                  value={player.info.name || ''}
                                  onChange={(e) => setAddTeamForm((prev) => {
                                    const players = [...prev.players];
                                    players[idx] = { ...players[idx], info: { ...players[idx].info, name: e.target.value } };
                                    return { ...prev, players };
                                  })}
                                  placeholder="Player name"
                                  className={missing ? 'border-destructive focus-visible:ring-destructive' : ''}
                                />
                                {missing && <p className="text-[11px] text-destructive">Required</p>}
                              </div>
                            );
                          })()
                        )}
                      </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Optional: invite the team manager to self-register + pay. Only
                  offered when this division has a registration to point them at. */}
              {registration?._id && (
                <div className="space-y-3 border-t pt-4">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={addTeamForm.sendInvite}
                      onChange={(e) => setAddTeamForm((prev) => ({ ...prev, sendInvite: e.target.checked }))}
                    />
                    <span className="text-sm">
                      <span className="font-medium">Send registration invite to team manager</span>
                      <span className="block text-xs text-muted-foreground">
                        Emails the manager a link to sign up, complete registration for this
                        division, and pay. Creates a pending registered team.
                      </span>
                    </span>
                  </label>
                  {addTeamForm.sendInvite && (
                    <div className="space-y-3 pl-6">
                      <div className="space-y-1">
                        <Label htmlFor="invite-email">
                          Manager Email <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="invite-email"
                          type="email"
                          value={addTeamForm.inviteEmail}
                          onChange={(e) => setAddTeamForm((prev) => ({ ...prev, inviteEmail: e.target.value }))}
                          placeholder="manager@example.com"
                          className={(addTeamValidation.inviteEmailMissing || addTeamValidation.inviteEmailInvalid) ? 'border-destructive focus-visible:ring-destructive' : ''}
                        />
                        {addTeamValidation.inviteEmailMissing && <p className="text-[11px] text-destructive">Required</p>}
                        {addTeamValidation.inviteEmailInvalid && <p className="text-[11px] text-destructive">Enter a valid email</p>}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="invite-first">First Name</Label>
                          <Input id="invite-first" value={addTeamForm.inviteFirstName} onChange={(e) => setAddTeamForm((prev) => ({ ...prev, inviteFirstName: e.target.value }))} placeholder="Optional" />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="invite-last">Last Name</Label>
                          <Input id="invite-last" value={addTeamForm.inviteLastName} onChange={(e) => setAddTeamForm((prev) => ({ ...prev, inviteLastName: e.target.value }))} placeholder="Optional" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Registration is past its end date — inviting needs an explicit
                      acknowledgment (stamped on the RT as allowLateRegistration). */}
                  {addTeamValidation.registrationClosed && (
                    <div className="rounded-md border border-warning/30 bg-warning/10 p-3 space-y-2">
                      <p className="text-xs text-warning font-medium">This registration is closed (past its end date).</p>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={addTeamForm.ignoreDeadline}
                          onChange={(e) => setAddTeamForm((prev) => ({ ...prev, ignoreDeadline: e.target.checked }))}
                        />
                        <span className="text-xs text-warning">
                          <span className="font-medium">Ignore registration deadline</span>: the manager can
                          still be invited and complete registration after the deadline.
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Force: place the team on the division immediately, bypassing
                      payment/waiver (handled on paper). Confirmation on toggle. */}
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={addTeamForm.forcePlace}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setForceConfirmOpen(true); // confirm before enabling
                        } else {
                          setAddTeamForm((prev) => ({ ...prev, forcePlace: false }));
                        }
                      }}
                    />
                    <span className="text-sm">
                      <span className="font-medium">Force team onto division</span>
                      <span className="block text-xs text-muted-foreground">
                        Marks the team accepted and shows it on the public division page immediately,
                        even without payment or a signed waiver (e.g. handled on paper).
                      </span>
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}

          {!addTeamValidation.valid && addTeamValidation.errors.length > 0 && (
            <p className="text-xs text-destructive px-1">{addTeamValidation.errors[0]}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddTeamOpen(false)}>Cancel</Button>
            <Button onClick={onAddTeam} disabled={isRegistrationLoading || !addTeamValidation.valid}>Add Team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force-onto-division confirmation (shown when the checkbox is toggled ON) */}
      <Dialog open={forceConfirmOpen} onOpenChange={(open) => { if (!open) setForceConfirmOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Force team onto division?</DialogTitle>
            <DialogDescription>
              The team will be marked <strong>accepted</strong> and shown on the public division page
              immediately, even though they haven&apos;t paid or signed the waiver. Use this when
              payment/waiver are handled outside Staty (e.g. on paper). You can record a payment
              later from the team page.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForceConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                setAddTeamForm((prev) => ({ ...prev, forcePlace: true }));
                setForceConfirmOpen(false);
              }}
            >
              Force onto division
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit game dialog (Round Robin) */}
      <Dialog open={editGameOpen} onOpenChange={setEditGameOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Game</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Home Team</Label>
              <Select value={editGameForm.homeTeamId} onValueChange={(v) => setEditGameForm((prev) => ({ ...prev, homeTeamId: v }))}>
                <SelectTrigger><SelectValue placeholder="Select home team" /></SelectTrigger>
                <SelectContent>
                  {divisionTeams.map((team) => (
                    <SelectItem key={team._id} value={team._id}>{getTeamDisplayName(team)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Away Team</Label>
              <Select value={editGameForm.awayTeamId} onValueChange={(v) => setEditGameForm((prev) => ({ ...prev, awayTeamId: v }))}>
                <SelectTrigger><SelectValue placeholder="Select away team" /></SelectTrigger>
                <SelectContent>
                  {divisionTeams.map((team) => (
                    <SelectItem key={team._id} value={team._id}>{getTeamDisplayName(team)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={editGameForm.dateKey}
                onChange={(e) => setEditGameForm((prev) => ({ ...prev, dateKey: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Select value={editGameForm.locationId} onValueChange={(v) => setEditGameForm((prev) => ({ ...prev, locationId: v }))}>
                <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>
                  {selectableLocations.map((loc) => (
                    <SelectItem key={loc._id} value={loc._id}>{getLocationDisplayName(loc)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Select value={String(editGameForm.startMinutes)} onValueChange={(v) => setEditGameForm((prev) => ({ ...prev, startMinutes: Number(v) }))}>
                  <SelectTrigger><SelectValue placeholder="Select time" /></SelectTrigger>
                  <SelectContent>
                    {quarterTicks.map((minute) => (
                      <SelectItem key={`edit-start-${minute}`} value={String(minute)}>{formatMinutesForGameTime(minute)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Duration</Label>
                <Select value={String(editGameForm.duration)} onValueChange={(v) => setEditGameForm((prev) => ({ ...prev, duration: Number(v) }))}>
                  <SelectTrigger><SelectValue placeholder="Select duration" /></SelectTrigger>
                  <SelectContent>
                    {[15, 30, 45, 60, 75, 90, 105, 120].map((d) => (
                      <SelectItem key={`edit-dur-${d}`} value={String(d)}>{d} minutes</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Home Score</Label>
                <Input type="text" inputMode="numeric" placeholder="—" value={editGameForm.homeScore}
                  onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setEditGameForm((prev) => ({ ...prev, homeScore: v === '' ? '' : Number(v) })); }} />
              </div>
              <div className="space-y-2">
                <Label>Away Score</Label>
                <Input type="text" inputMode="numeric" placeholder="—" value={editGameForm.awayScore}
                  onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setEditGameForm((prev) => ({ ...prev, awayScore: v === '' ? '' : Number(v) })); }} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditGameOpen(false)}>Cancel</Button>
            <Button onClick={onSaveEditGame}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
